/**
 * Palantir PMS - Software para la gestiÃƒÂ³n de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creaciÃƒÂ³n / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso estÃƒÂ¡ restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import NewRatePricePopup from "../Components/NewRatePricePopup";
import NewStayConstraintPopup from "../Components/NewStayConstraintPopup"; // Ã¢Å“â€¦ NUEVO - PARTIMOS DE AQUÃƒÂ PARA AÃƒâ€˜ADIR LOS POP UPS DE MIN Y MAX STAY
import RemoveRatePricesPopup from "../Components/RemoveRatePricesPopup";
import AddStopSellPopup from "../Components/AddStopSellPopup";
import RemoveStopSellPopup from "../Components/RemoveStopSellPopup";
import ComplexFormulaPopup from "../Components/ComplexFormulaPopup";
import RoomRateEditForm from "./RoomRateEditForm";





import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
  Brush,
} from "recharts";
import StayLengthTable from "../Components/StayLengthTable"; // ajusta la ruta si es necesario
import ApplyScrapedPricesPopup from "../Components/ApplyScrapedPricesPopup";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import {
  currencyAmountStep,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
  quantizeCurrencyAmount,
} from "../currency/currencyPrecision";
import { DEMO_BUSINESS_UNIT_ID, isDemoMode, readDemoUser } from "../demo/demoMode";
import { demoReferenceDate } from "../demo/demoSnapshot";
import "../EditRatePrice.css";

export function formatRatePriceNumber(value, currencyCode, currencyCatalog = []) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";
  return amount.toFixed(getCurrencyMinorUnits(currencyCode, currencyCatalog));
}

export function formatRatePriceAmount(value, currencyCode, currencyCatalog = []) {
  const formatted = formatRatePriceNumber(value, currencyCode, currencyCatalog);
  return formatted === "-" ? formatted : `${formatted} ${currencyCode}`;
}

export function normalizeRatePriceInput(value, currencyCode, currencyCatalog = []) {
  if (!hasValidCurrencyPrecision(value, currencyCode, currencyCatalog)) return null;
  return quantizeCurrencyAmount(value, currencyCode, currencyCatalog);
}



const EditRatePrice = ({ roomRateId = null, forcedTab = null, hideViewTabs = false }) => {

  const demoModeActive = isDemoMode();
  const currentUser = demoModeActive ? readDemoUser() : JSON.parse(localStorage.getItem("palantir_user"));
  const permissions = currentUser?.permissions || {};
  const demoReadOnlyTitle = "Demo de solo consulta: no se pueden modificar tarifas.";
  const referenceDate = demoModeActive ? demoReferenceDate() : "";

  const { themeId } = usePmsTheme();
  const { baseCurrency: contextCurrency, currencyCatalog } = useCurrencyDisplay();
  const isLightTheme = themeId === "light";
  


  const normalizeStayRange = (min, max) => {
    const normalizedMin = min ?? 1;
    const normalizedMax = max ?? 999;

    if (normalizedMin === 1 && normalizedMax === 999) {
      return "1 / 999";
    }

    return `${normalizedMin} / ${normalizedMax}`;
  };

  const parseIsoDate = (raw) => {
    const str = String(raw || "").slice(0, 10);
    const [year, month, day] = str.split("-").map((n) => Number(n));
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    const date = new Date(year, month - 1, day);
    if (!Number.isFinite(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
  };
  const toIsoLocalDate = (date) => {
    if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const { id: routeRateId } = useParams();
  const activeRateId = roomRateId || routeRateId;
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [demoLoadError, setDemoLoadError] = useState("");
  const baseCurrency = formData?.room_rate?.currency || contextCurrency;
  const currencyMinorUnits = getCurrencyMinorUnits(baseCurrency, currencyCatalog);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessUnitId = demoModeActive ? DEMO_BUSINESS_UNIT_ID : (searchParams.get("business_unit_id") || "1");
  const [sisterCategoriesText, setSisterCategoriesText] = useState("");
  const [priceHistory, setPriceHistory] = useState([]);
  const [stopSells, setStopSells] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showStopSellPopup, setShowStopSellPopup] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    if (demoModeActive) return "internal";
    if (forcedTab === "market" || forcedTab === "internal") {
      return forcedTab;
    }
    const saved = localStorage.getItem("palantir_lastRateTab");
    return saved === "market" ? "market" : "internal";
  });

  const [marketPrices, setMarketPrices] = useState([]);
  const [showApplyPricePopup, setShowApplyPricePopup] = useState(false);
  const [showScraperApplyPopup, setShowScraperApplyPopup] = useState(false);
  const isMarketDataMode = !demoModeActive && (activeTab === "market" || showApplyPricePopup || showScraperApplyPopup);
  const [adjustType, setAdjustType] = useState("equal");
  const [adjustValue, setAdjustValue] = useState(0);
  const [adjustUnit, setAdjustUnit] = useState("absolute");
  const [isApplying, setIsApplying] = useState(false);
  const [showRemoveStopSellPopup, setShowRemoveStopSellPopup] = useState(false);
  const [focusDateInternal, setFocusDateInternal] = useState(null);
  const [internalNavigatorDate, setInternalNavigatorDate] = useState(null);
  const [focusDateMarket, setFocusDateMarket] = useState(null);
  const [settings, setSettings] = useState({});
  
  const [scrapedPrices, setScrapedPrices] = useState([]);
  const [scraperTargets, setScraperTargets] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedOTAs, setSelectedOTAs] = React.useState([]);
  const [isOtaFilterOpen, setIsOtaFilterOpen] = React.useState(false);
  const [tempSelectedOTAs, setTempSelectedOTAs] = React.useState([]);
  const [showEditRatePopup, setShowEditRatePopup] = useState(false);

  

  // ===== Prices chart & actions =====
  // [Apply] UI state for derived-link popup
  const [showLinkDerivedPopup, setShowLinkDerivedPopup] = useState(false);
  const [baseCandidates, setBaseCandidates] = useState([]);
  const createInitialLinkForm = () => ({
    base_rate_id: null,
    adjustment_type: "percent", // "percent" | "absolute"
    adjustment_value: ""
  });
  const [linkForm, setLinkForm] = useState(createInitialLinkForm);
  const [derivedRateMode, setDerivedRateMode] = useState("simple");

  const [unlinkLoading, setUnlinkLoading] = React.useState(false);

    // Ã°Å¸â€œÅ’ Lista de todas las roomRates de la BU para detectar si esta tarifa es madre con hijas
    const [allRoomRates, setAllRoomRates] = useState([]);

    useEffect(() => {
      if (demoModeActive) return;
      axios
        .get(`${process.env.REACT_APP_API_URL}/api/roomRates/all`, {
          params: { business_unit_id: businessUnitId },
        })
        .then((res) => setAllRoomRates(res.data || []))
        .catch((err) =>
          console.error("Ã¢ÂÅ’ Error cargando roomRates para detectar hijas:", err)
        );
    }, [businessUnitId, demoModeActive]);

  const allRoomRatesById = React.useMemo(() => {
    const index = new Map();
    (allRoomRates || []).forEach((roomRate) => {
      if (roomRate?.id != null) index.set(Number(roomRate.id), roomRate);
    });
    return index;
  }, [allRoomRates]);

  async function handleUnlinkFromBase() {
    if (demoModeActive || isDemoMode()) return;
    const rate = formData?.room_rate;
    const rateId = rate?.id;
    if (!rateId) return;

    const ok = window.confirm(
      "This will unlink this rate (simple or formula) and allow manual editing. Continue?"
    );
    if (!ok) return;

    try {
      setUnlinkLoading(true);

      // Ã°Å¸Â§Â© Determinar tipo de vÃƒÂ­nculo
      if (rate?.has_formula_link) {
        console.log("[Unlink] Formula link detected Ã¢â€ â€™ DELETE /unlink-formula");
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/derived-rates/unlink-formula/${rateId}`
        );
      } else {
        console.log("[Unlink] Simple base link detected Ã¢â€ â€™ PATCH /unlink");
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/derived-rates/unlink/${rateId}`
        );
      }

      // Ã°Å¸Â§Â¹ Limpiar vinculaciÃƒÂ³n en el estado local
      setFormData((prev) => ({
        ...prev,
        room_rate: {
          ...prev.room_rate,
          base_rate_id: null,
          adjustment_type: null,
          adjustment_value: null,
          has_formula_link: false,
        },
      }));

      setBaseRateMeta?.(null);

      alert("Unlinked successfully. You can now edit prices manually.");
    } catch (err) {
      console.error("Ã¢ÂÅ’ Error unlinking:", err);
      alert("Error unlinking rate. Please check console.");
    } finally {
      setUnlinkLoading(false);
    }
  }

  

  const baseRateId = formData?.room_rate?.base_rate_id;
  const [baseRateMeta, setBaseRateMeta] = React.useState(null);
  const demoBaseRate = demoModeActive
    ? (allRoomRatesById.get(Number(baseRateId)) || formData?.room_rate?.base_rate || null)
    : null;

  React.useEffect(() => {
    if (demoModeActive) {
      setBaseRateMeta(demoBaseRate);
      return;
    }
    let abort = false;

    async function loadBaseRate() {
      try {
        setBaseRateMeta(null);
        if (!baseRateId) return;
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/roomRates/${baseRateId}`
        );
        if (!abort) setBaseRateMeta(data);
      } catch (err) {
        console.error("Ã¢ÂÅ’ Error cargando base rate meta:", err);
      }
    }

    loadBaseRate();
    return () => { abort = true; };
  }, [baseRateId, demoModeActive, demoBaseRate]);


  // Trigger to reload current rate data after edits/actions
  const [reloadFlag, setReloadFlag] = useState(0);


  // [Apply] Load candidate base rates when popup opens
  useEffect(() => {
    if (demoModeActive || !showLinkDerivedPopup) return;
    const loadCandidates = async () => {
      try {
        const current = formData?.room_rate;
        // Ã¢Å¡Â Ã¯Â¸Â Usamos el businessUnitId de la sesiÃƒÂ³n/URL, no el de la rate
        const buId = businessUnitId; 
        console.log("[Apply] Loading base candidates. Current:", {
          id: current?.id, currency: current?.currency, buId
        });

        // Ã¢Å“â€¦ Ahora sÃƒÂ­, pedimos las tarifas de la BU activa
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/roomRates/all`,
          { params: { business_unit_id: buId } }
        );


        // Ã¢Å“â€¦ Solo madres (no derivadas), misma currency, activas
        const filtered = (data || []).filter(r =>
          r.id !== current?.id &&
          r.status === "active" &&
          r.currency === current?.currency
        );


        // Ã¢Å“â€¦ Evitar duplicados de hermanas Ã¢â€ â€™ quedarnos con 1 por group_key
        const uniqueByGroup = Array.from(
          new Map(filtered.map(r => [r.group_key || r.id, r])).values()
        );

        const sortedCandidates = [...uniqueByGroup].sort((a, b) => {
          const aLabel = `${a?.name || ""} ${a?.rate_code || ""}`.trim();
          const bLabel = `${b?.name || ""} ${b?.rate_code || ""}`.trim();
          return aLabel.localeCompare(bLabel, undefined, { sensitivity: "base" });
        });

        setBaseCandidates(sortedCandidates);

        console.log("[Apply] baseCandidates (same BU & currency):",
          filtered.map(r => ({ id: r.id, name: r.name, cat: r.room_category_id })));
      } catch (err) {
        console.error("[Apply] Error loading base candidates:", err);
        alert("Error loading base rate candidates.");
      }
    };
    loadCandidates();
  }, [showLinkDerivedPopup, demoModeActive]);

  useEffect(() => {
    if (demoModeActive) return;
    async function fetchSettings() {
      try {
        const buId = formData?.room_rate?.business_unit_id || businessUnitId;
        const { data } = await axios.get(`/api/hotel-settings?business_unit_id=${buId}`);
        setDerivedRateMode(data?.derived_rate_mode || "simple");
        setSettings(data || {});
        console.log("[DerivedRateMode] Loaded:", data?.derived_rate_mode);
      } catch (err) {
        console.error("[DerivedRateMode] Error loading:", err);
      }
    }
    fetchSettings();
  }, [formData?.room_rate?.business_unit_id, reloadFlag, demoModeActive]);






  const handleApplyMarketPrices = async () => {
    if (demoModeActive || isDemoMode()) return;
    let targetDates = [];

    if (dateSelectionMode === "range") {
      if (!rangeStart || !rangeEnd) {
        alert("Please select a valid date range.");
        return;
      }

      const start = parseIsoDate(rangeStart);
      const end = parseIsoDate(rangeEnd);
      if (!start || !end || end < start) {
        alert("Please select a valid date range.");
        return;
      }
      const current = new Date(start);

      while (current <= end) {
        const dateStr = toIsoLocalDate(current);
        targetDates.push(dateStr);
        current.setDate(current.getDate() + 1);
      }
    } 

    if (dateSelectionMode === "individual") {
      targetDates = selectedDates;
    }


    const marketDates = marketPrices.map(p => p.start_date);
    const validDates = targetDates.filter(d => marketDates.includes(d));



    const skipped = targetDates.filter(d => !validDates.includes(d));
    if (skipped.length > 0) {
      alert("Some dates were skipped because no market price was found:\n\n" + skipped.join("\n"));
    }


    

    if (validDates.length === 0) {
      alert("No valid market prices found for selected dates.");
      return;
    }


    const roomRateId = formData?.room_rate_id || formData?.room_rate?.id;
    if (!roomRateId) {
      alert("Missing room rate ID.");
      return;
    }

    const toInsert = [];

    if (adjustType !== "equal") {
      const adjustment = Number(String(adjustValue || "").replace(",", "."));
      if (!Number.isFinite(adjustment)) {
        alert("Please enter a valid adjustment.");
        return;
      }
      if (
        adjustUnit === "absolute" &&
        !hasValidCurrencyPrecision(adjustValue, baseCurrency, currencyCatalog)
      ) {
        alert(`${baseCurrency} accepts ${currencyMinorUnits} decimal places.`);
        return;
      }
    }

    for (const date of validDates) {
      const market = marketPrices.find((p) => p.start_date === date);
      if (!market) continue;

      let price = parseFloat(market.price);

      if (adjustType === "above" || adjustType === "below") {
        const sign = adjustType === "above" ? 1 : -1;
        const val = Number(String(adjustValue || "").replace(",", "."));

        if (adjustUnit === "absolute") {
          price += sign * val;
        } else {
          price *= 1 + sign * (val / 100);
        }
      }

      price = quantizeCurrencyAmount(price, baseCurrency, currencyCatalog);
      if (price === null) continue;

      toInsert.push({
        start_date: date,
        end_date: date,
        price,
        room_rate_id: roomRateId,
      });

    }

    const verifyPayloadAppliedAfterTimeout = async (payload) => {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/rate-prices/history/${roomRateId}`,
        { withCredentials: true }
      );

      const historyRows = Array.isArray(data) ? data : [];
      const targetDay = String(payload.start_date || "").slice(0, 10);
      const matchingRow = historyRows.find((row) => {
        const rowDay = String(row?.stay_date || row?.start_date || "").slice(0, 10);
        return String(row.room_rate_id) === String(roomRateId) && rowDay === targetDay;
      });

      if (!matchingRow) return false;

      const actualPrice = Number(matchingRow.price);
      const expectedPrice = Number(payload.price);
      if (!Number.isFinite(actualPrice) || !Number.isFinite(expectedPrice)) return false;

      const tolerance = 1 / (10 ** currencyMinorUnits);
      return (
        Math.abs(actualPrice - expectedPrice) < tolerance ||
        Math.abs(actualPrice - Math.round(expectedPrice)) < tolerance
      );
    };

    try {
      for (const payload of toInsert) {
        console.log("Ã°Å¸â€œÂ¤ Posting:", payload);
        console.log("[FRONT] Payload enviado a backend:", payload);
        try {
          await axios.post(`${process.env.REACT_APP_API_URL}/api/rate-prices`, payload);
        } catch (error) {
          if (error?.response?.status === 504) {
            try {
              const alreadyApplied = await verifyPayloadAppliedAfterTimeout(payload);
              if (alreadyApplied) {
                continue;
              }
            } catch (verificationError) {
              console.error("Error verifying applied market price after timeout:", verificationError);
            }
          }
          throw error;
        }
      }

      alert("Prices applied successfully.");
      resetPopupState();
      setShowApplyPricePopup(false);
      setReloadFlag((v) => v + 1);

    } catch (error) {
      console.error("Ã¢ÂÅ’ Error applying prices:", error);
      alert("Error applying prices. Check console for details.");
    }
  };

  const handleApplyScrapedPrices = async (payload) => {
    if (demoModeActive || isDemoMode()) return;
    // Get room_rate_id from DB-backed form state (never from URL)
    const roomRateId = formData?.room_rate_id || formData?.room_rate?.id;

    console.log("[Apply][XOTELO] onApply payload (raw):", payload, "| roomRateId:", roomRateId);

    const finalPayload = { ...payload, room_rate_id: roomRateId };

    if (!finalPayload.room_rate_id) {
      console.error("[Apply][XOTELO] Missing room_rate_id in EditRatePrice");
      alert("Missing room rate ID for Apply Scraped Prices.");
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/scraper-targets/apply-scraped`,
        finalPayload
      );
      console.log("[Apply][XOTELO] applied:", res.data);
      alert("Prices applied from TripAdvisor.");
      setShowScraperApplyPopup(false);
      setReloadFlag((v) => v + 1);
    } catch (error) {
      console.error("[Apply][XOTELO] error:", error);
      alert("Error applying scraped prices (Xotelo). Check console.");
    }
  };




  const [dateSelectionMode, setDateSelectionMode] = useState("range");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  const [showMinStayPopup, setShowMinStayPopup] = useState(false);
  const [showMaxStayPopup, setShowMaxStayPopup] = useState(false);
  
  const [showRemoveTariffPopup, setShowRemoveTariffPopup] = useState(false);

  useEffect(() => {
    if (demoModeActive) {
      setActiveTab("internal");
      return;
    }
    if (forcedTab === "market" || forcedTab === "internal") {
      setActiveTab(forcedTab);
    }
  }, [forcedTab, demoModeActive]);

  
  const DAY_MS = 24 * 60 * 60 * 1000;
  const INTERNAL_CHART_WINDOW_DAYS = 90;

  const availableStayRanges = React.useMemo(
    () => [
      ...new Set(
        (priceHistory || []).map((p) => {
          const min = p.min_stay == null ? 1 : p.min_stay;
          const max = p.max_stay == null ? 999 : p.max_stay;
          return `${min} / ${max}`;
        })
      ),
    ],
    [priceHistory]
  );

  const resetPopupState = () => {
    setRangeStart("");
    setRangeEnd("");
    setSelectedDates([]);
    setAdjustType("equal");
    setAdjustValue(0);
    setAdjustUnit("absolute");
  };

  // Map of your real prices by date (highest available for that day).
  const realPricesByDate = React.useMemo(() => {
    if (!isMarketDataMode) return {};

    const out = {};

    (priceHistory || []).forEach((p) => {
      const current = new Date(`${String(p.start_date).slice(0, 10)}T00:00:00`);
      const end = new Date(`${String(p.end_date).slice(0, 10)}T00:00:00`);
      const price = Number.parseFloat(p.price);
      if (!Number.isFinite(current.getTime()) || !Number.isFinite(end.getTime()) || !Number.isFinite(price)) return;

      for (let d = new Date(current); d <= end; d.setDate(d.getDate() + 1)) {
        const dateStr = toIsoLocalDate(d);
        const existing = out[dateStr];
        if (!existing || price > existing.price) {
          out[dateStr] = { price, source: "max" };
        }
      }
    });

    return out;
  }, [priceHistory, isMarketDataMode]);

  // O(1) lookup set for stop-sell dates on current room rate.
  const stopSellDateSet = React.useMemo(() => {
    const set = new Set();
    const rateId = Number(formData?.room_rate?.id || formData?.room_rate_id);

    const toIso = (raw) => {
      const parsed = parseIsoDate(raw);
      return parsed ? toIsoLocalDate(parsed) : null;
    };

    (stopSells || []).forEach((ss) => {
      if (Number.isFinite(rateId) && Number(ss.room_rate_id) !== rateId) return;

      const stayDate = toIso(ss.stay_date);
      if (stayDate) {
        set.add(stayDate);
        return;
      }

      const startIso = toIso(ss.start_date);
      const endIso = toIso(ss.end_date);
      if (!startIso || !endIso) return;

      const startDate = new Date(`${startIso}T00:00:00`);
      const endDate = new Date(`${endIso}T00:00:00`);
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const iso = toIsoLocalDate(d);
        if (iso) set.add(iso);
      }
    });

    return set;
  }, [stopSells, formData?.room_rate?.id, formData?.room_rate_id]);

  // Expanded market prices by date.
  const marketPricesByDate = React.useMemo(() => {
    if (!isMarketDataMode) return {};

    const out = {};

    (marketPrices || []).forEach((p) => {
      const startDate = new Date(`${String(p.start_date).slice(0, 10)}T00:00:00`);
      const endDate = new Date(`${String(p.end_date).slice(0, 10)}T00:00:00`);
      const price = Number.parseFloat(p.price);
      if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(endDate.getTime()) || !Number.isFinite(price)) return;

      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateStr = toIsoLocalDate(d);
        out[dateStr] = {
          price,
          source: p.source,
          origin: p.origin,
          currency: p.currency,
        };
      }
    });

    return out;
  }, [marketPrices, isMarketDataMode]);

  const expandedMarketPrices = React.useMemo(() => {
    if (!isMarketDataMode) return [];

    const out = [];

    (marketPrices || []).forEach((p) => {
      const startDate = new Date(`${String(p.start_date).slice(0, 10)}T00:00:00`);
      const endDate = new Date(`${String(p.end_date).slice(0, 10)}T00:00:00`);
      const price = Number.parseFloat(p.price);
      if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(endDate.getTime()) || !Number.isFinite(price)) return;

      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        out.push({
          date: toIsoLocalDate(d),
          price,
          source: p.source,
          origin: p.origin,
          currency: p.currency,
        });
      }
    });

    return out;
  }, [marketPrices, isMarketDataMode]);

  const marketChartData = React.useMemo(() => {
    if (!Array.isArray(marketPrices) || marketPrices.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const maxEndMs = (marketPrices || []).reduce((max, p) => {
      const t = new Date(`${String(p.end_date).slice(0, 10)}T00:00:00`).getTime();
      return Number.isFinite(t) && t > max ? t : max;
    }, today.getTime());

    const isInactive = formData?.room_rate?.status !== "active";
      const rows = [];
      const endDate = new Date(maxEndMs);
      endDate.setHours(0, 0, 0, 0);
      const cursor = new Date(today);
      cursor.setHours(0, 0, 0, 0);

      while (cursor.getTime() <= endDate.getTime()) {
        const dateStr = toIsoLocalDate(cursor);
        const marketRow = marketPricesByDate[dateStr];

        let status = "SELLABLE";
        if (stopSellDateSet.has(dateStr)) status = "STOP SELL";
        else if (isInactive) status = "INACTIVE";

        rows.push({
          date: dateStr,
          Market: marketRow ? marketRow.price : null,
          YourRate: realPricesByDate[dateStr]?.price ?? null,
          status,
        });

        cursor.setDate(cursor.getDate() + 1);
        cursor.setHours(0, 0, 0, 0);
      }

    return rows;
  }, [marketPrices, marketPricesByDate, realPricesByDate, stopSellDateSet, formData?.room_rate?.status, DAY_MS]);



    // Build categories text from already-loaded room rates (no extra API call)
    useEffect(() => {
      const currentId = Number(formData?.room_rate?.id);
      const fallbackText = formData?.room_rate?.room_category?.name || "N/A";

      if (demoModeActive && Array.isArray(formData?.room_rate?.sister_categories)) {
        const names = Array.from(new Set(formData.room_rate.sister_categories.map((category) => category.name).filter(Boolean)));
        setSisterCategoriesText(names.join(" + ") || fallbackText);
        return;
      }

      if (!Number.isFinite(currentId)) {
        setSisterCategoriesText(fallbackText);
        return;
      }

      const all = Array.isArray(allRoomRates) ? allRoomRates : [];
      if (!all.length) {
        setSisterCategoriesText(fallbackText);
        return;
      }

      const picked = all.find((r) => Number(r?.id) === currentId);
      if (!picked) {
        setSisterCategoriesText(fallbackText);
        return;
      }

      const gk = picked?.group_key || `__NO_GK__:${picked?.id}`;
      const sisters = all.filter((r) => (r?.group_key || `__NO_GK__:${r?.id}`) === gk);
      const names = Array.from(new Set(sisters.map((r) => r?.room_category?.name).filter(Boolean)));

      setSisterCategoriesText(names.length > 0 ? names.join(" + ") : fallbackText);
    }, [allRoomRates, formData?.room_rate?.id, formData?.room_rate?.room_category?.name, formData?.room_rate?.sister_categories, demoModeActive]);










  // Load all mandatory demo collections together. A legacy snapshot must never
  // turn missing prices or stop sells into a misleading empty/open chart.
  useEffect(() => {
    if (!demoModeActive) return;
    let cancelled = false;
    const loadDemoDetail = async () => {
      setLoading(true);
      setDemoLoadError("");
      setFormData(null);
      setPriceHistory([]);
      setStopSells([]);
      setAllRoomRates([]);
      setFocusDateInternal(null);
      setInternalNavigatorDate(null);
      try {
        if (!/^[1-9]\d*$/.test(String(activeRateId || ""))) {
          throw new Error("La tarifa solicitada no existe en la demo.");
        }
        const [detail, history, all, stops] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/roomRates/${activeRateId}`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/rate-prices/history/${activeRateId}`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/roomRates/all`, { params: { business_unit_id: DEMO_BUSINESS_UNIT_ID } }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/stop-sells`, { params: { business_unit_id: DEMO_BUSINESS_UNIT_ID } }),
        ]);
        if (cancelled) return;
        if (!detail?.data || Number(detail.data.id) !== Number(activeRateId)) {
          throw new Error("La tarifa solicitada no existe en la demo.");
        }
        if (![history?.data, all?.data, stops?.data].every(Array.isArray) || !demoReferenceDate()) {
          throw new Error("Actualiza el exportador y el JSON de la demo para mostrar los detalles de tarifas.");
        }
        setFormData({ room_rate: detail.data, room_rate_id: detail.data.id });
        setPriceHistory(history.data);
        setAllRoomRates(all.data);
        setStopSells(stops.data.filter((row) => Number(row.room_rate_id) === Number(activeRateId)));
      } catch (error) {
        if (cancelled) return;
        setDemoLoadError(error?.response?.data?.error || error?.message || "No se han podido cargar los detalles de la tarifa.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadDemoDetail();
    return () => { cancelled = true; };
  }, [activeRateId, reloadFlag, demoModeActive]);

  useEffect(() => {
    if (demoModeActive) return;
    const loadRoomRateAndPrices = async () => {
      setLoading(true);

      try {
        if (!activeRateId) {
          setLoading(false);
          return;
        }

        const roomRateRequest = axios.get(`${process.env.REACT_APP_API_URL}/api/roomRates/${activeRateId}`);
        const historyRequest = axios.get(`${process.env.REACT_APP_API_URL}/api/rate-prices/history/${activeRateId}`);

        const [roomRateRes, historyRes] = await Promise.all([roomRateRequest, historyRequest]);
        const roomRate = roomRateRes?.data;

        if (!roomRate) {
          alert("Room Rate not found.");
          return;
        }

        setFormData({
          room_rate: roomRate,
          room_rate_id: roomRate.id,
          start_date: "",
          end_date: "",
          price: "",
          min_stay: "",
          max_stay: ""
        });

        setPriceHistory(Array.isArray(historyRes?.data) ? historyRes.data : []);
      } catch (error) {
        console.error("Error loading room rate and prices:", error);
      } finally {
        setLoading(false);
      }
    };

    loadRoomRateAndPrices();
  }, [activeRateId, reloadFlag, demoModeActive]);

  

  useEffect(() => {
    if (demoModeActive) return;
    const roomRateId = Number(formData?.room_rate?.id);
    if (!Number.isFinite(roomRateId)) {
      setStopSells([]);
      return;
    }

    axios.get(`${process.env.REACT_APP_API_URL}/api/stop-sells`, {
      params: { business_unit_id: businessUnitId }
    })
      .then((response) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        const filtered = rows.filter((ss) => Number(ss?.room_rate_id) === roomRateId);
        setStopSells(filtered);
      })
      .catch((error) => {
        console.error("Error loading stop sells:", error);
      });
  }, [formData?.room_rate?.id, businessUnitId, reloadFlag, demoModeActive]);
  
  useEffect(() => {
    if (demoModeActive || activeTab !== "market") return;

    const roomRateId = formData?.room_rate_id;
    if (!roomRateId) return;

    axios.get(`${process.env.REACT_APP_API_URL}/api/market-rate-prices/${roomRateId}`)
      .then((res) => {
        console.log("Ã°Å¸â€œÅ  Precios de mercado cargados:", res.data);
        setMarketPrices(res.data);
      })
      .catch((err) => {
        console.error("Ã¢ÂÅ’ Error al cargar precios de mercado:", err);
      });
  }, [activeTab, formData?.room_rate_id, reloadFlag, demoModeActive]);

  useEffect(() => {
    if (demoModeActive || activeTab !== "setup") return;

    const roomRateId = formData?.room_rate_id;
    if (!roomRateId) return;

    axios.get(`${process.env.REACT_APP_API_URL}/api/scraper-targets/scraped-market-prices/${roomRateId}`)
      .then(res => {
        console.log("Ã°Å¸â€œÂ¥ Scraped prices received:", res.data);
        console.log("Ã°Å¸Â§Âª Ejemplo de uno:", res.data?.[0]);
        setScrapedPrices(res.data);
      })
      .catch(err => {
        console.error("Ã¢ÂÅ’ Error fetching scraped market prices:", err);
      });
  }, [activeTab, formData?.room_rate_id, demoModeActive]);

  useEffect(() => {
    if (!isMarketDataMode) return;
    if (!formData?.room_rate_id) return;

    axios.get(`${process.env.REACT_APP_API_URL}/api/scraper-targets`, {
      params: {
        room_rate_id: formData.room_rate_id,
       business_unit_id: businessUnitId   // Ã°Å¸â€˜Ë† tienes este id en contexto
      }
    })
      .then(res => {
        console.log("Ã°Å¸â€œÂ¡ Scraper targets:", res.data);
        setScraperTargets(res.data);
      })
      .catch(err => {
        console.error("Ã¢ÂÅ’ Error loading scraper targets:", err);
      });
  }, [isMarketDataMode, formData?.room_rate_id, businessUnitId]);


  const scraperTargetsById = React.useMemo(() => {
    const map = new Map();
    (scraperTargets || []).forEach((target) => {
      if (target?.id != null) map.set(Number(target.id), target);
    });
    return map;
  }, [scraperTargets]);

  // Group scraped prices by target_id (memoized to avoid expensive work on internal tab)
  const groupedByTarget = React.useMemo(() => {
    if (!isMarketDataMode) return {};

    const grouped = {};
    (scrapedPrices || []).forEach((entry) => {
      const targetId = Number(entry?.scraper_target_id);
      const date = entry?.start_date;
      if (!Number.isFinite(targetId) || !date) return;

      const target = scraperTargetsById.get(targetId);
      const params = target?.params ?? {};

      if (!grouped[targetId]) {
        grouped[targetId] = {
          meta: {
            scraper_target_id: targetId,
            source_type: target?.source_type ?? "Unknown",
            mode: target?.mode ?? "unknown",
            mode_descr: target?.mode === "hotel"
              ? (() => {
                  const slug = params?.url?.split("/")[5]?.replace(".es.html", "") ?? "";
                  return slug
                    .split("-")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ") || "Hotel";
                })()
              : (params?.destination || "Area"),
            length_of_stay: params.length_of_stay ?? "?",
            adults: params.adults ?? "?",
            children: params.children ?? "?"
          },
          pricesByDate: {}
        };
      }

      grouped[targetId].pricesByDate[date] = {
        price: entry.price,
        hotels: Array.isArray(entry.debug_json) ? entry.debug_json : [],
        length_of_stay: entry.length_of_stay || 1
      };
    });

    return grouped;
  }, [isMarketDataMode, scrapedPrices, scraperTargetsById]);

  // Collect and sort future scraped dates
  const sortedScrapedDates = React.useMemo(() => {
    if (!isMarketDataMode) return [];

    const allDatesSet = new Set();
    (scrapedPrices || []).forEach((entry) => {
      if (entry?.start_date) allDatesSet.add(entry.start_date);
    });

    return Array.from(allDatesSet)
      .filter((date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const d = new Date(`${String(date).slice(0, 10)}T00:00:00`);
        d.setHours(0, 0, 0, 0);
        return d >= today;
      })
      .sort();
  }, [isMarketDataMode, scrapedPrices]);




  useEffect(() => {
    if (demoModeActive) return;
    if (forcedTab === "market" || forcedTab === "internal") return;
    if (activeTab === "market") {
      localStorage.removeItem("palantir_lastRateTab");
    }
  }, [activeTab, forcedTab, demoModeActive]);

  // Ã°Å¸Â§Â© Estados para tabla combinada (Xotelo + Scraper)
  const [xoteloRows, setXoteloRows] = React.useState([]);     // crudos desde /api/xotelo/rows
  const [mergedColumns, setMergedColumns] = React.useState({}); // columnas por target (scraper + xotelo)
  const [mergedDates, setMergedDates] = React.useState([]);     // fechas ÃƒÂºnicas ordenadas (>= hoy)

  // ==============================
  // XOTELO: helpers de carga/fusiÃƒÂ³n
  // ==============================

  // A) GET RAW Xotelo por room_rate
    async function fetchXoteloMarketPrices(roomRateId, { from, to, ota } = {}) {
    if (demoModeActive || isDemoMode()) return [];
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/scraper-targets/xotelo-market-prices/${roomRateId}`,
        { params: { from, to, ota } }
      );
      console.log("[XOTELO] rows:", res.data?.length, res.data?.slice?.(0, 5));
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      console.error("Ã¢ÂÅ’ [XOTELO] fetch error:", err?.message || err);
      return [];
    }
  }


  // B) Meta de cabecera a partir del target
  function buildTargetMetaFromTarget(target) {
    const p = target?.params || {};
    const childrenCount =
      typeof p.children_ages === "string" && p.children_ages.trim() !== ""
        ? p.children_ages.split(",").filter(Boolean).length
        : (typeof p.children === "number" ? p.children : 0);

    return {
      mode: "api", // mostrarÃƒÂ¡ "API" en cabecera
      mode_descr: `Xotelo - ${p.name || p.hotel_key || "Hotel"}`,
      length_of_stay: Number(p.length_of_stay || 1),
      adults: Number(p.adults || 2),
      children: Number(childrenCount || 0),
    };
  }

  // C) Agrupar RAW Xotelo al mismo shape que usa tu tabla actual
  // grouped[targetId] = { meta, pricesByDate: { 'YYYY-MM-DD': { price, hotels:[], length_of_stay } } }
  // Ã¢Å“â€¦ NUEVO: una columna por cada OTA (no agregamos entre OTAs)
  // clave de columna = xotelo:<targetId>:<ota_code>
  function groupXoteloRows(rows, targetsById) {
    const grouped = {};
    const dateSet = new Set();

    for (const row of rows || []) {
      if (!row) continue;

      const targetId = row.scraper_target_id;
      const date = row.start_date;              // "YYYY-MM-DD"
      if (!date) continue;
      dateSet.add(date);

      const otaCode = String(row.ota_code || "UNKNOWN");
      const otaName = String(row.ota_name || otaCode);
      const colKey  = `xotelo:${targetId}:${otaCode}`;

      // meta base desde el target
      const t = targetsById[targetId];
      const baseMeta = buildTargetMetaFromTarget(t); // { mode:"api", mode_descr:"Xotelo Ã¢â‚¬â€œ Ã¢â‚¬Â¦", LOS, adults, children }

      // inicializar columna-OTA
      if (!grouped[colKey]) {
        grouped[colKey] = {
          meta: {
            ...baseMeta,
            ota_code: otaCode,
            ota_name: otaName,
          },
          pricesByDate: {},
        };
      }

      // una celda = una OTA ese dÃƒÂ­a (precio por noche con impuestos)
      const gross = Number(row.rate_gross);
      const net   = Number(row.rate_net);
      const tax   = row.tax != null ? Number(row.tax) : null;
      const los   = Number(row.length_of_stay || baseMeta.length_of_stay || 1);

      if (Number.isFinite(gross)) {
        grouped[colKey].pricesByDate[date] = {
          price: quantizeCurrencyAmount(
            gross,
            row.currency || baseCurrency,
            currencyCatalog
          ),
          hotels: [
            {
              name: otaName,
              code: otaCode,
              price: gross,
              net,
              tax,
              currency: row.currency,
            },
          ],
          length_of_stay: los,
        };
      }
    }

    // no necesitamos calcular mÃƒÂ­nimos: ya es 1 OTA Ã¢â€ â€™ 1 precio
    return { grouped, dates: Array.from(dateSet) };
  }


  // D) Fusionar Xotelo con lo que ya existe (scraper)
  function mergeMarketBuckets({ existingGrouped, existingDates, xoteloGrouped, xoteloDates }) {
    const merged = { ...(existingGrouped || {}) };

    // Fechas (union)
    const dateSet = new Set([...(existingDates || []), ...(xoteloDates || [])]);
    const mergedDates = Array.from(dateSet).sort(); // YYYY-MM-DD ordena bien lexicogrÃƒÂ¡ficamente

    // Targets (concat y min)
    for (const [tId, bucket] of Object.entries(xoteloGrouped || {})) {
      if (!merged[tId]) {
        merged[tId] = { meta: bucket.meta, pricesByDate: {} };
      }
      for (const [date, cell] of Object.entries(bucket.pricesByDate || {})) {
        if (!merged[tId].pricesByDate[date]) {
          merged[tId].pricesByDate[date] = { ...cell };
        } else {
          // Si ya hay datos ese dÃƒÂ­a (por ej. del scraper), concatenamos hoteles y recalculamos min
          const prev = merged[tId].pricesByDate[date];
          prev.hotels = [...(prev.hotels || []), ...(cell.hotels || [])];
          const minVal = Math.min(
            ...(prev.hotels || []).map(h => Number(h.price)).filter(Number.isFinite)
          );
          if (Number.isFinite(minVal)) {
            prev.price = quantizeCurrencyAmount(minVal, baseCurrency, currencyCatalog);
          }
          // Conserva length_of_stay existente o toma el mayor
          prev.length_of_stay = Math.max(Number(prev.length_of_stay || 1), Number(cell.length_of_stay || 1));
        }
      }
    }

    return { merged, mergedDates };
  }

  // E) Orquestador: trae Xotelo, agrupa y fusiona con el estado actual
  // Ã¢Å¡â„¢Ã¯Â¸Â Trae Xotelo, APLANA a "1 columna por OTA" y fusiona con lo existente (scraper)
  async function loadAndMergeXotelo({ roomRateId, targets, existingGrouped, existingDates }) {
    try {
      // 1) mapa de targets
      const targetsById = {};
      (targets || []).forEach(t => { targetsById[t.id] = t; });

      // 2) traer RAW XOTELO
      const rows = await fetchXoteloMarketPrices(roomRateId);
      setXoteloRows(rows);

      // 3) APLANAR: construir columnas por OTA
      const xoteloFlatColumns = {};  // { 'xotelo:<targetId>:<ota_code>': { meta, pricesByDate } }
      const xoteloDateSet = new Set();

      for (const row of rows || []) {
        if (!row) continue;
        const targetId = row.scraper_target_id;
        const otaCode  = String(row.ota_code || "UNKNOWN");
        const otaName  = String(row.ota_name || otaCode);
        const colKey   = `xotelo:${targetId}:${otaCode}`;
        const date     = row.start_date;           // "YYYY-MM-DD"
        if (date) xoteloDateSet.add(date);

        // meta base desde el target
        const t = targetsById[targetId] || {};
        const p = t.params || {};
        const childrenCount =
          typeof p.children_ages === "string" && p.children_ages.trim() !== ""
            ? p.children_ages.split(",").filter(Boolean).length
            : (typeof p.children === "number" ? p.children : 0);

        if (!xoteloFlatColumns[colKey]) {
          xoteloFlatColumns[colKey] = {
            meta: {
              mode: "api",
              mode_descr: `TripAdvisor - ${p.name || p.hotel_key || "Hotel"}`,
              length_of_stay: Number(row.length_of_stay || p.length_of_stay || 1),
              adults: Number(p.adults || 2),
              children: Number(childrenCount || 0),
              ota_code: otaCode,
              ota_name: otaName,
            },

            pricesByDate: {}
          };
        }

        if (date) {
          const gross = Number(row.rate_gross);
          const net   = Number(row.rate_net);
          const tax   = row.tax != null ? Number(row.tax) : null;
          const los   = Number(row.length_of_stay || 1);

          if (Number.isFinite(gross)) {
            xoteloFlatColumns[colKey].pricesByDate[date] = {
              price: quantizeCurrencyAmount(
                gross,
                row.currency || baseCurrency,
                currencyCatalog
              ), // precio por noche con impuestos
              hotels: [{
                name: otaName,
                code: otaCode,
                price: gross,
                net,
                tax,
                currency: row.currency,
              }],
              length_of_stay: los,
            };
          }
        }
      }

      console.log("[XOTELO] flat cols:", Object.keys(xoteloFlatColumns));

      // 4) FUSIÃƒâ€œN: columnas y fechas
      const merged = { ...(existingGrouped || {}), ...xoteloFlatColumns };
      const mergedDates = Array.from(new Set([...(existingDates || []), ...Array.from(xoteloDateSet)])).sort();

      // 5) guardar en estado que usa el render
      setMergedColumns(merged);
      setMergedDates(mergedDates);

      console.log("[XOTELO] merge done. columns:", Object.keys(merged), "dates:", mergedDates.slice(0,5));
      return { merged, mergedDates };
    } catch (err) {
      console.error("Ã¢ÂÅ’ [XOTELO] loadAndMergeXotelo:", err?.message || err);
      return { merged: existingGrouped || {}, mergedDates: existingDates || [] };
    }
  }



  useEffect(() => {
    if (!isMarketDataMode) return;
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
          params: { business_unit_id: businessUnitId }
        });
        console.log("Ã¢Å¡â„¢Ã¯Â¸Â Settings RMS recibidos:", res.data);
        setSettings(res.data);
      } catch (err) {
        console.error("Ã¢ÂÅ’ Error al cargar settings:", err);
      }
    };

    fetchSettings();
  }, [isMarketDataMode, businessUnitId]);

  // Ã¢Å¡â„¢Ã¯Â¸Â Disparar la carga de XOTELO cuando ya tengamos:
  //  - roomRateId
  //  - targets (de /api/scraper-targets?room_rate_id=...)
  //  - groupedByTarget + sortedScrapedDates (lo que ya construyes con el scraper)
  // Ã¢Å¡â„¢Ã¯Â¸Â Disparar XOTELO aunque no haya datos del scraper (permite modo Xotelo-solo)
  const xoteloLoadedRef = React.useRef(false);

  React.useEffect(() => {
    if (!isMarketDataMode) return;
    try {
      const rId = formData?.room_rate_id;
      const tgs = scraperTargets;

      if (!rId) {
        console.log("[XOTELO] skip: falta formData.room_rate_id");
        return;
      }
      if (!Array.isArray(tgs) || tgs.length === 0) {
        console.log("[XOTELO] skip: scraperTargets vacÃƒÂ­o");
        return;
      }
      if (xoteloLoadedRef.current) {
        // evita dobles cargas si ya fusionamos
        return;
      }

      // Ã¢Å“â€¦ Ahora permitimos que XOTELO cargue y pinte aunque NO haya groupedByTarget/sortedScrapedDates
      const existingGrouped = groupedByTarget || {};
      const existingDates = Array.isArray(sortedScrapedDates) ? sortedScrapedDates : [];

      console.log("[XOTELO] launching merge with:", {
        roomRateId: rId,
        targets: tgs.length,
        existingDates: existingDates.length
      });

      loadAndMergeXotelo({
        roomRateId: rId,
        targets: tgs,
        existingGrouped,
        existingDates,
      })
        .then(() => {
          xoteloLoadedRef.current = true;
          console.log("[XOTELO] merge completed");
        })
        .catch(err => {
          console.error("Ã¢ÂÅ’ [XOTELO] merge failed:", err?.message || err);
        });
    } catch (e) {
      console.error("Ã¢ÂÅ’ [XOTELO] effect error:", e?.message || e);
    }
  // Ã°Å¸â€˜â€° mantenemos las deps para que, si mÃƒÂ¡s tarde llegan datos del scraper, volvamos a fusionar una sola vez
  }, [isMarketDataMode, formData?.room_rate_id, scraperTargets, groupedByTarget, sortedScrapedDates]);





  const handleSubmit = async (e) => {
    e.preventDefault();
    if (demoModeActive || isDemoMode()) return;
    const normalizedPrice = normalizeRatePriceInput(
      formData.price,
      baseCurrency,
      currencyCatalog
    );
    if (normalizedPrice === null) {
      alert(`${baseCurrency} accepts ${currencyMinorUnits} decimal places.`);
      return;
    }
    try {
      const payload = {
        price: normalizedPrice,
        start_date: formData.start_date,
        end_date: formData.end_date,
        min_stay: formData.min_stay === "" ? null : parseInt(formData.min_stay),
        max_stay: formData.max_stay === "" ? null : parseInt(formData.max_stay),
        room_rate_id: formData.room_rate_id || formData.room_rate?.id
      };

      await axios.put(`${process.env.REACT_APP_API_URL}/api/rate-prices/${activeRateId}`, payload);

      alert("Rate Price actualizado correctamente.");
      navigate(`/revenue-management?business_unit_id=${businessUnitId}`);
    } catch (error) {
        console.error("Ã¢ÂÅ’ Error al actualizar el Rate Price:", error);
        if (error.response) {
          console.error("Ã°Å¸Â§Â  Detalles del error del backend:", error.response.data);
          alert(error.response.data?.error || "Error al actualizar el Rate Price.");
        } else {
          alert("Error al actualizar el Rate Price.");
        }
      }
      
  };

  const chartWindow = React.useMemo(() => {
    const start = parseIsoDate(referenceDate) || new Date();
    start.setHours(0, 0, 0, 0);
    const days = 365;
    const dates = [];
    const indexByIso = {};
    const tsByIso = {};
    const cursor = new Date(start);

    for (let i = 0; i < days; i += 1) {
      const iso = toIsoLocalDate(cursor);
      const ts = cursor.getTime();
      dates.push({ ts, iso });
      indexByIso[iso] = i;
      tsByIso[iso] = ts;
      cursor.setDate(cursor.getDate() + 1);
      cursor.setHours(0, 0, 0, 0);
    }

    return {
      days,
      startTs: dates[0]?.ts ?? start.getTime(),
      endTs: dates[dates.length - 1]?.ts ?? start.getTime(),
      startIso: dates[0]?.iso,
      endIso: dates[dates.length - 1]?.iso,
      dates,
      indexByIso,
      tsByIso,
    };
  }, [referenceDate]);

  const internalChartModel = React.useMemo(() => {
    const seriesMap = {};
    let lastPricedTs = null;

    (priceHistory || []).forEach((entry) => {
      const key = normalizeStayRange(entry.min_stay, entry.max_stay);
      if (!seriesMap[key]) seriesMap[key] = new Array(chartWindow.days).fill(null);

      const rawPrice = Number.parseFloat(entry.price);
      if (!Number.isFinite(rawPrice)) return;

      const startDate = parseIsoDate(entry.start_date);
      const endDate = parseIsoDate(entry.end_date);
      if (!startDate || !endDate) return;

      const startIso = toIsoLocalDate(startDate);
      const endIso = toIsoLocalDate(endDate);
      if (!startIso || !endIso) return;

      const clampedStartIso =
        startIso > chartWindow.endIso ? null :
        startIso < chartWindow.startIso ? chartWindow.startIso :
        startIso;
      const clampedEndIso =
        endIso < chartWindow.startIso ? null :
        endIso > chartWindow.endIso ? chartWindow.endIso :
        endIso;
      if (!clampedStartIso || !clampedEndIso) return;

      const fromIdx = chartWindow.indexByIso[clampedStartIso];
      const toIdx = chartWindow.indexByIso[clampedEndIso];
      if (fromIdx == null || toIdx == null || toIdx < fromIdx) return;

      const clampedEndTs = chartWindow.tsByIso[clampedEndIso];
      if (Number.isFinite(clampedEndTs) && (!Number.isFinite(lastPricedTs) || clampedEndTs > lastPricedTs)) {
        lastPricedTs = clampedEndTs;
      }

      for (let i = fromIdx; i <= toIdx; i += 1) {
        seriesMap[key][i] = rawPrice;
      }
    });

    const parseStayRange = (key) => {
      const [minRaw, maxRaw] = String(key || "").split("/").map((x) => x?.trim());
      const min = Number.parseInt(minRaw, 10);
      const max = Number.parseInt(maxRaw, 10);
      return {
        min: Number.isFinite(min) ? min : 1,
        max: Number.isFinite(max) ? max : 999,
      };
    };

    const seriesKeys = Object.keys(seriesMap).sort((a, b) => {
      const ar = parseStayRange(a);
      const br = parseStayRange(b);
      if (ar.min !== br.min) return ar.min - br.min;
      return ar.max - br.max;
    });

  const palette = [
      "#223A7A", "#3DB6E9", "#63C1C7", "#5374B8", "#2E8FA8",
      "#4A9E98", "#7C90A7", "#6D86B8", "#8BBFC8", "#35549A",
    ];
    const colorByKey = Object.fromEntries(
      seriesKeys.map((key, idx) => [key, palette[idx % palette.length]])
    );

    // Keep each stay-range color, but stitch consecutive-day handoffs
    // when the active stay range changes.
    const isFinitePrice = (value) =>
      value !== null && value !== undefined && Number.isFinite(value);

    const stitchedSeriesMap = Object.fromEntries(
      seriesKeys.map((key) => [key, [...seriesMap[key]]])
    );

    let previousPrimaryKey = null;
    for (let idx = 0; idx < chartWindow.days; idx += 1) {
      const availableKeys = seriesKeys.filter((key) =>
        isFinitePrice(stitchedSeriesMap[key]?.[idx])
      );

      if (!availableKeys.length) {
        previousPrimaryKey = null;
        continue;
      }

      const primaryKey =
        previousPrimaryKey && availableKeys.includes(previousPrimaryKey)
          ? previousPrimaryKey
          : availableKeys[0];
      const primaryValue = stitchedSeriesMap[primaryKey][idx];

      if (
        previousPrimaryKey &&
        previousPrimaryKey !== primaryKey &&
        isFinitePrice(primaryValue) &&
        stitchedSeriesMap[previousPrimaryKey][idx] == null
      ) {
        stitchedSeriesMap[previousPrimaryKey][idx] = primaryValue;
      }

      previousPrimaryKey = primaryKey;
    }

    const visibleMaxTs = Number.isFinite(lastPricedTs)
      ? Math.min(chartWindow.endTs, Math.max(chartWindow.startTs, lastPricedTs))
      : chartWindow.endTs;

    const allRows = chartWindow.dates.map((d, idx) => {
      const row = { date: d.iso, dateTs: d.ts };
      seriesKeys.forEach((key) => {
        const value = stitchedSeriesMap[key][idx];
        row[key] = value;
      });
      return row;
    });

    const rows = allRows.filter((row) => row.dateTs <= visibleMaxTs);
    const rowIndexByDate = Object.fromEntries(rows.map((row, idx) => [row.date, idx]));
    const visibleEndIso = rows[rows.length - 1]?.date || chartWindow.endIso;

    const visibleStopDates = Array.from(stopSellDateSet)
      .filter((date) => date >= chartWindow.startIso && date <= visibleEndIso)
      .sort();

    const stopSellRanges = [];
    let rangeStartTs = null;
    let previousIso = null;
    let previousIndex = null;

    const pushRange = () => {
      if (rangeStartTs == null || previousIso == null || previousIndex == null) return;
      const prevTs = chartWindow.tsByIso[previousIso];
      if (!Number.isFinite(prevTs)) return;
      const nextTs = chartWindow.dates[previousIndex + 1]?.ts;
      const width = Number.isFinite(nextTs) ? nextTs - prevTs : DAY_MS;
      stopSellRanges.push({
        x1: rangeStartTs,
        x2: prevTs + width,
      });
    };

    visibleStopDates.forEach((isoDate) => {
      const idx = chartWindow.indexByIso[isoDate];
      const ts = chartWindow.tsByIso[isoDate];
      if (idx == null || !Number.isFinite(ts)) return;

      if (rangeStartTs == null) {
        rangeStartTs = ts;
        previousIso = isoDate;
        previousIndex = idx;
        return;
      }

      if (previousIndex !== null && idx === previousIndex + 1) {
        previousIso = isoDate;
        previousIndex = idx;
        return;
      }

      pushRange();
      rangeStartTs = ts;
      previousIso = isoDate;
      previousIndex = idx;
    });

    pushRange();

    const hasSomeValue = rows.some((row) => seriesKeys.some((key) => isFinitePrice(row[key])));

    return {
      rows,
      allRows,
      rowIndexByDate,
      seriesKeys,
      colorByKey,
      stopSellRanges,
      minTs: chartWindow.startTs,
      maxTs: visibleMaxTs,
      navigatorMaxTs: chartWindow.endTs,
      hasData: hasSomeValue,
    };
  }, [priceHistory, chartWindow, stopSellDateSet, DAY_MS]);

  React.useEffect(() => {
    if (!internalChartModel.rows.length) {
      if (internalNavigatorDate !== null) setInternalNavigatorDate(null);
      return;
    }

    const firstDate = internalChartModel.rows[0].date;
    const lastDate = internalChartModel.rows[internalChartModel.rows.length - 1].date;

    if (!internalNavigatorDate) {
      setInternalNavigatorDate(firstDate);
      return;
    }

    if (internalNavigatorDate < firstDate) {
      setInternalNavigatorDate(firstDate);
      return;
    }

    if (internalNavigatorDate > lastDate) {
      setInternalNavigatorDate(lastDate);
    }
  }, [internalChartModel.rows, internalNavigatorDate]);

  const internalChartViewport = React.useMemo(() => {
    const baseRows = internalChartModel.rows || [];
    const totalRows = baseRows.length;
    if (!totalRows) {
      return {
        rows: [],
        minTs: chartWindow.startTs,
        maxTs: chartWindow.startTs,
        maxTsWithTail: chartWindow.startTs + DAY_MS,
        stopSellRanges: [],
      };
    }

    const maxStartIndex = Math.max(0, totalRows - INTERNAL_CHART_WINDOW_DAYS);
    const defaultStartDate = baseRows[0].date;
    const anchorDate = internalNavigatorDate || defaultStartDate;
    const anchorIndex = internalChartModel.rowIndexByDate[anchorDate];

    let startIndex = 0;
    if (anchorIndex == null) {
      startIndex = anchorDate > baseRows[totalRows - 1].date ? maxStartIndex : 0;
    } else {
      startIndex = Math.min(maxStartIndex, Math.max(0, anchorIndex));
    }

    const endIndex = Math.min(totalRows - 1, startIndex + INTERNAL_CHART_WINDOW_DAYS - 1);
    const rows = baseRows.slice(startIndex, endIndex + 1);
    const minTs = rows[0]?.dateTs ?? chartWindow.startTs;
    const maxTs = rows[rows.length - 1]?.dateTs ?? chartWindow.startTs;
    const nextTs = baseRows[endIndex + 1]?.dateTs;
    const maxTsWithTail = Number.isFinite(nextTs) ? nextTs : maxTs + DAY_MS;

    const stopSellRanges = internalChartModel.stopSellRanges.filter(
      (range) => range.x2 >= minTs && range.x1 <= maxTsWithTail
    );

    return {
      rows,
      minTs,
      maxTs,
      maxTsWithTail,
      stopSellRanges,
      startDate: rows[0]?.date || null,
      endDate: rows[rows.length - 1]?.date || null,
    };
  }, [
    internalChartModel,
    internalNavigatorDate,
    chartWindow.startTs,
    DAY_MS,
    INTERNAL_CHART_WINDOW_DAYS,
  ]);

  const navigatorBrushWindow = React.useMemo(() => {
    const total = internalChartModel.allRows?.length || 0;
    if (!total) return { startIndex: 0, endIndex: 0 };

    const maxIndex = total - 1;
    const rawStart = internalChartViewport.startDate
      ? chartWindow.indexByIso[internalChartViewport.startDate]
      : 0;
    const rawEnd = internalChartViewport.endDate
      ? chartWindow.indexByIso[internalChartViewport.endDate]
      : Math.min(maxIndex, INTERNAL_CHART_WINDOW_DAYS - 1);

    const startIndex = Number.isFinite(rawStart)
      ? Math.max(0, Math.min(maxIndex, rawStart))
      : 0;
    const endIndexCandidate = Number.isFinite(rawEnd)
      ? rawEnd
      : (startIndex + INTERNAL_CHART_WINDOW_DAYS - 1);
    const endIndex = Math.max(startIndex, Math.min(maxIndex, endIndexCandidate));

    return { startIndex, endIndex };
  }, [
    internalChartModel.allRows,
    internalChartViewport.startDate,
    internalChartViewport.endDate,
    chartWindow.indexByIso,
    INTERNAL_CHART_WINDOW_DAYS,
  ]);

  const navigatorRafRef = React.useRef(null);
  const queuedNavigatorDateRef = React.useRef(null);

  const flushNavigatorDrag = React.useCallback(() => {
    navigatorRafRef.current = null;
    const nextDate = queuedNavigatorDateRef.current;
    queuedNavigatorDateRef.current = null;
    if (!nextDate) return;
    setInternalNavigatorDate((prev) => (prev === nextDate ? prev : nextDate));
  }, []);

  const handleNavigatorBrushChange = React.useCallback((range) => {
    const startIndex = Number(range?.startIndex);
    if (!Number.isFinite(startIndex)) return;
    const maxIndex = (internalChartModel.allRows?.length || 1) - 1;
    const clampedIndex = Math.max(0, Math.min(maxIndex, Math.floor(startIndex)));
    const nextDate = internalChartModel.allRows?.[clampedIndex]?.date;
    if (!nextDate) return;

    queuedNavigatorDateRef.current = nextDate;
    if (navigatorRafRef.current != null) return;

    const canUseRaf =
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function";

    if (canUseRaf) {
      navigatorRafRef.current = window.requestAnimationFrame(flushNavigatorDrag);
      return;
    }

    flushNavigatorDrag();
  }, [internalChartModel.allRows, flushNavigatorDrag]);

  React.useEffect(() => () => {
    if (
      navigatorRafRef.current != null &&
      typeof window !== "undefined" &&
      typeof window.cancelAnimationFrame === "function"
    ) {
      window.cancelAnimationFrame(navigatorRafRef.current);
    }
  }, []);

  const focusHighlightRange = React.useMemo(() => {
    if (!focusDateInternal) return null;
    const start = chartWindow.tsByIso[focusDateInternal];
    const idx = chartWindow.indexByIso[focusDateInternal];
    if (!Number.isFinite(start) || idx == null) return null;
    const nextTs = chartWindow.dates[idx + 1]?.ts;
    const end = Number.isFinite(nextTs) ? nextTs : start + DAY_MS;
    return { start, end };
  }, [focusDateInternal, chartWindow, DAY_MS]);

  const bestPriceByDate = React.useMemo(() => {
    const normalizeMinStayValue = (value) => {
      const n = Number(value);
      if (!Number.isFinite(n) || n < 1) return 1;
      return Math.floor(n);
    };

    const normalizeMaxStayValue = (value) => {
      if (value == null) return null;
      const n = Number(value);
      if (!Number.isFinite(n) || n < 1) return null;
      return Math.floor(n);
    };

    const out = {};
    (priceHistory || []).forEach((entry) => {
      const start = parseIsoDate(entry.start_date);
      const end = parseIsoDate(entry.end_date);
      const price = Number.parseFloat(entry.price);
      if (!start || !end || !Number.isFinite(price)) return;

      const minStay = normalizeMinStayValue(entry.min_stay);
      const maxStay = normalizeMaxStayValue(entry.max_stay);
      const stayKey = normalizeStayRange(entry.min_stay, entry.max_stay);

      // The demo tooltip can only refer to this chart window. Do not expand
      // long source rules for invisible past/future days in the visitor's UI.
      const startTs = demoModeActive ? Math.max(start.getTime(), chartWindow.startTs) : start.getTime();
      const endTs = demoModeActive ? Math.min(end.getTime(), chartWindow.endTs) : end.getTime();
      for (let cursor = new Date(startTs); cursor.getTime() <= endTs; cursor.setDate(cursor.getDate() + 1)) {
        const iso = toIsoLocalDate(cursor);
        const existing = out[iso];
        if (!existing || price < existing.price) {
          out[iso] = {
            price,
            minStay,
            maxStay,
            stayKey,
          };
        }
      }
    });

    return out;
  }, [priceHistory, demoModeActive, chartWindow]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const toIsoDate = (rawLabel) => {
      if (typeof rawLabel === "number") return toIsoLocalDate(new Date(rawLabel));
      if (typeof rawLabel === "string" && /^\d+$/.test(rawLabel)) {
        return toIsoLocalDate(new Date(Number(rawLabel)));
      }
      if (typeof rawLabel === "string") {
        const parsed = parseIsoDate(rawLabel);
        if (parsed) return toIsoLocalDate(parsed);
        return rawLabel.slice(0, 10);
      }
      return String(rawLabel || "").slice(0, 10);
    };

    const payloadIsoDate = String(payload?.[0]?.payload?.date || "").slice(0, 10);
    const isoDate = payloadIsoDate || toIsoDate(label);
    const hasStopSell = stopSellDateSet.has(isoDate);

    const bestEntry = bestPriceByDate[isoDate];

    const formatStayLabel = (minStay, maxStay) => {
      const maxLabel = maxStay == null ? "∞" : maxStay;
      return `${minStay ?? 1} / ${maxLabel}`;
    };

    const fallbackItems = (payload || [])
      .filter((item) => item?.value !== null && item?.value !== undefined && Number.isFinite(Number(item?.value)))
      .sort((a, b) => Number(a.value) - Number(b.value))
      .map((item) => ({
        stayLabel: String(item.name || "").trim() || "Other",
        price: Number(item.value),
        color: item.color,
      }));

    const entries = bestEntry
      ? [
          {
            stayLabel: formatStayLabel(bestEntry.minStay, bestEntry.maxStay),
            price: bestEntry.price,
            color: internalChartModel.colorByKey[bestEntry.stayKey],
          },
        ]
      : fallbackItems;

    return (
      <div
        className="erp-chartTooltip p-2 rounded"
        style={{ minWidth: 210 }}
      >
        <p className="mb-2"><strong>{isoDate}</strong></p>
        {entries.length > 0 ? (
          entries.map((item, index) => (
            <div key={`${item.stayLabel}-${index}`} className="mb-1">
              <span style={{ color: item.color || "#3db6e9" }}>•</span>{" "}
              <strong>{item.stayLabel}</strong>: {formatRatePriceAmount(
                item.price,
                baseCurrency,
                currencyCatalog
              )}
            </div>
          ))
        ) : (
          <div className="mb-1 text-muted" style={{ fontSize: "0.8rem" }}>
            No rate data for this date.
          </div>
        )}
        {hasStopSell && <div className="erp-chartTooltipDanger mt-2 fw-bold">STOP SELL ACTIVE</div>}
      </div>
    );
  };

  const MarketTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div
        className="erp-chartTooltip p-2 rounded"
      >
        <p className="mb-2"><strong>{label}</strong></p>
        {payload.map((item, idx) => {
          const raw = Number(item?.value);
          const showValue = Number.isFinite(raw)
            ? formatRatePriceAmount(raw, baseCurrency, currencyCatalog)
            : "No rate";
          return (
            <div key={`${item.name}-${idx}`} className="mb-1">
              <span style={{ color: item.color }}>*</span>{" "}
              <strong>{item.name}</strong>: {showValue}
            </div>
          );
        })}
      </div>
    );
  };


  // Ã°Å¸â€˜â€¡ alias de render: si hay datos fusionados (Xotelo+Scraper), ÃƒÂºsalos; si no, usa los del scraper
  
  const tableColumns = React.useMemo(
    () => (Object.keys(mergedColumns || {}).length ? mergedColumns : (groupedByTarget || {})),
    [mergedColumns, groupedByTarget]
  );
  const tableDates = React.useMemo(
    () => ((mergedDates && mergedDates.length) ? mergedDates : (sortedScrapedDates || [])),
    [mergedDates, sortedScrapedDates]
  );

  // Ã°Å¸â€Å½ Opciones disponibles de OTA (TripAdvisor/Xotelo)
  const otaOptions = React.useMemo(() => {
    try {
      const names = [];
      Object.values(tableColumns || {}).forEach((col) => {
        const meta = col?.meta || {};
        if (meta.source_type === "xotelo" || meta.mode === "api") {
          const name = (meta.ota_name || "").trim();
          if (name) names.push(name);
        }
      });
      return [...new Set(names)].sort((a, b) => a.localeCompare(b));
    } catch (err) {
      console.error("[Filter][OTA options] build error:", err, { tableColumns });
      return [];
    }
  }, [tableColumns]);

  // Ã°Å¸â€”â€šÃ¯Â¸Â Columnas visibles segÃƒÂºn filtro (si no hay filtro Ã¢â€ â€™ todas)
  //     - Mostramos SOLO columnas de Xotelo cuyo ota_name estÃƒÂ© seleccionado.
  //     - Cuando hay filtro, ocultamos tambiÃƒÂ©n columnas que no sean de Xotelo.
  const tableColumnsFiltered = React.useMemo(() => {
    const filters = selectedOTAs || [];
    if (!filters.length) return tableColumns;

    const out = {};
    Object.entries(tableColumns || {}).forEach(([key, col]) => {
      const meta = col?.meta || {};
      const isXotelo = meta.source_type === "xotelo" || meta.mode === "api";
      const name = (meta.ota_name || "").trim();
      if (isXotelo && filters.includes(name)) {
        out[key] = col;
      }
    });
    return out;
  }, [selectedOTAs, tableColumns]);



  // Ã°Å¸â€Â§ FUENTES PARA APPLY (una entrada por columna visible en la tabla) Ã¢â‚¬â€ SIN HOOKS
  const applySources = React.useMemo(() => {
    const out = [];
    try {
      Object.entries(tableColumns || {}).forEach(([key, col]) => {
        if (!col || !col.meta) return;
        const meta = col.meta;

        const modeLabel =
          meta.mode === "hotel" ? "Hotel" :
          meta.mode === "area"  ? "Area"  :
          meta.mode === "api"   ? "Hotel by OTA" :
          (meta.mode || "-");

        const labelParts = [modeLabel];
        if (meta.mode_descr) labelParts.push(String(meta.mode_descr).replace(/\.html?$/, ""));
        if (meta.ota_name)   labelParts.push(meta.ota_name);
        const label = labelParts.filter(Boolean).join(" - ");

        const kind = String(key).startsWith("xotelo:") ? "xotelo" : "scraper";

        let targetId = null; // compat para apply legacy
        if (kind === "scraper") {
          targetId = Number(meta.scraper_target_id ?? key);
        } else {
          const m = /^xotelo:(\d+):/.exec(String(key));
          targetId = m ? Number(m[1]) : null;
        }

        out.push({
          key,
          kind,
          label,
          targetId,
          pricesByDate: col.pricesByDate || {},
          ota_name: meta.ota_name,
          hotel_key: meta.hotel_key,
          room_rate_id: meta.room_rate_id
        });
      });
    } catch (err) {
      console.error("[ApplySources] build error:", err, { tableColumns });
    }
    return out;
  }, [tableColumns]);

  // --- derived lock helpers ---
  const rate = formData?.room_rate;

  // Detect both simple base-rate links and formula links.
  const isDerived =
    (
      !!rate?.base_rate_id &&
      !!rate?.adjustment_type &&
      rate?.adjustment_value !== null &&
      rate?.adjustment_value !== undefined
    ) ||
    !!rate?.has_formula_link;

  const isMother =
    Array.isArray(allRoomRates) &&
    allRoomRates.some((r) => r.base_rate_id === rate?.id);

  const formatRateDisplay = (roomRate, fallbackId = null) => {
    const id = roomRate?.id ?? fallbackId;
    if (id == null) return "-";
    const name = roomRate?.name || `#${id}`;
    const code = roomRate?.rate_code || `#${id}`;
    return `${name} (${code})`;
  };

  const simpleDependencyChain = (() => {
    if (!rate?.base_rate_id || rate?.has_formula_link) return [];

    const level1Id = Number(rate.base_rate_id);
    if (!Number.isInteger(level1Id) || level1Id <= 0) return [];

    const level1 = allRoomRatesById.get(level1Id) || baseRateMeta || null;
    const level1Text = level1 ? formatRateDisplay(level1, level1Id) : `#${level1Id}`;

    const level2Id = Number(level1?.base_rate_id);
    if (!Number.isInteger(level2Id) || level2Id <= 0) return [level1Text];

    const level2 = allRoomRatesById.get(level2Id);
    const level2Text = level2 ? formatRateDisplay(level2, level2Id) : `#${level2Id}`;

    return [level1Text, level2Text].filter(Boolean);
  })();

  const linkedToText = rate?.has_formula_link && rate?.formula_inputs?.length
    ? rate.formula_inputs
        .map((f) => `${f.base_rate_name} (${f.rate_code})`)
        .join(" + ")
    : simpleDependencyChain.length
      ? simpleDependencyChain.join(" -> ")
      : (baseRateMeta
        ? formatRateDisplay(baseRateMeta, rate?.base_rate_id)
        : (rate?.base_rate_id ? `#${rate.base_rate_id}` : "-"));

  const calcText = (() => {
    if (!isDerived) return "";
    if (rate.has_formula_link) {
      const formulaLabels = {
        average: "Average",
        weighted: "Weighted average",
        min: "Minimum",
        max: "Maximum",
        custom: "Custom formula",
      };
      return formulaLabels[rate.formula_type] || "Formula";
    }

    const adjustment = Number(rate.adjustment_value);
    if (!Number.isFinite(adjustment)) return "";
    const unit = rate.adjustment_type === "percent" ? "%" : ` ${rate?.currency || baseCurrency}`;
    return `${adjustment >= 0 ? "+" : ""}${adjustment}${unit}`;
  })();

  const handleBack = () => {
    const routerHistoryIndex = Number(window.history.state?.idx);
    let hasSameOriginReferrer = false;

    try {
      hasSameOriginReferrer = Boolean(document.referrer) && new URL(document.referrer).origin === window.location.origin;
    } catch (_error) {
      hasSameOriginReferrer = false;
    }

    if ((Number.isFinite(routerHistoryIndex) && routerHistoryIndex > 0) || hasSameOriginReferrer) {
      navigate(-1);
      return;
    }

    navigate(`/revenue-management-container/management?business_unit_id=${businessUnitId}`);
  };

  if (demoModeActive && demoLoadError) {
    return (
      <div className="erp-page erp-demo-loadError" role="alert">
        <p>{demoLoadError}</p>
        <button type="button" onClick={() => setReloadFlag((value) => value + 1)}>Reintentar</button>
        <button type="button" onClick={handleBack}>Volver a tarifas</button>
      </div>
    );
  }
  if (loading || !formData?.room_rate) {
    return <div role="status">{demoModeActive ? "Cargando detalles de la tarifa…" : "Loading rate price..."}</div>;
  }


  return (
    <>


      {/* Top tabs */}
      <div className={`erp-page${isLightTheme ? " erp-page--light" : ""}`}>
      {!hideViewTabs && (
      <div className="erp-tabs">
        <button
          onClick={() => {
            setActiveTab("internal");
            setShowScraperApplyPopup(false);
          }}
          className={`erp-tab ${activeTab === "internal" ? "erp-tab--active" : ""}`}
        >
          Internal Rate Prices
        </button>

        {!demoModeActive && !isDerived && ["RMS", "Scraper"].includes(settings.rms_source) && permissions["revenue.view_market_prices"] && (
          <button
            onClick={() => {
              setActiveTab("market");
              setShowScraperApplyPopup(false);
            }}
            className={`erp-tab ${activeTab === "market" ? "erp-tab--active" : ""}`}
          >
            Internal vs Market Rate Prices
          </button>
        )}
      </div>
      )}
      <div className="erp-shell">
        <div className={`erp-card erp-card--${activeTab}`}>
          {!roomRateId && (
            <button
              type="button"
              className="erp-cardBackBtn"
              onClick={handleBack}
              aria-label="Back to previous page"
              title="Back"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 5 8 12l7 7" />
                <path d="M9 12h10" />
              </svg>
            </button>
          )}
          {activeTab === "internal" && (
            <>
              <div className="erp-head">
                <h2
                  className="erp-title"
                  data-read-only={demoModeActive || undefined}
                  title={demoModeActive ? demoReadOnlyTitle : undefined}
                  onClick={() => { if (!demoModeActive && !isDemoMode()) setShowEditRatePopup(true); }}
                >
                  {formData.room_rate?.name} ({formData.room_rate?.rate_code})
                </h2>
                <div className="erp-subtitle">
                  {formData.room_rate?.client?.name || "Direct Reservations"} - {sisterCategoriesText || "N/A"}
                </div>
                <div className="erp-meta">
                  <span className={`erp-pill ${formData.room_rate?.status === "active" ? "erp-pill--ok" : "erp-pill--off"}`}>
                    {String(formData.room_rate?.status || "unknown").toUpperCase()}
                  </span>
                  <span className="erp-pill erp-pill--validity">
                    <span>{formData.room_rate?.valid_from?.slice(0, 10) || "-"}</span>
                    <span>to</span>
                    <span>{formData.room_rate?.valid_to?.slice(0, 10) || "\u221e"}</span>
                  </span>
                  {isDerived && <span className="erp-pill">Derived</span>}
                  {isMother && <span className="erp-pill">Base rate</span>}
                  {isDerived && (
                    <span className="erp-pill erp-pill--linked">
                      Linked to: <strong>{linkedToText}</strong>
                      {calcText ? ` | Calc: ${calcText}` : ""}
                    </span>
                  )}
                </div>

                <div className="erp-summaryChips" style={{ marginTop: 12 }}>
                  <span className="erp-chip"><strong>Status</strong>{String(formData?.room_rate?.status || "unknown").toUpperCase()}</span>
                  <span className="erp-chip"><strong>Currency</strong>{formData?.room_rate?.currency || baseCurrency}</span>
                  <span className="erp-chip"><strong>Guests</strong>{formData?.room_rate?.adults ?? "Any"} / {formData?.room_rate?.children ?? "Any"}</span>
                  <span className="erp-chip"><strong>Refundable</strong>{formData?.room_rate?.is_refundable ? "Yes" : "No"}</span>
                  <span className="erp-chip"><strong>Penalty</strong>{formData?.room_rate?.penalty_type || "-"}</span>
                  <span className="erp-chip"><strong>Dependency</strong>{isDerived ? linkedToText : "Independent"}</span>
                  {isDerived && <span className="erp-chip"><strong>Formula</strong>{calcText || "-"}</span>}
                </div>

                {!demoModeActive && showEditRatePopup && (
                  <RoomRateEditForm
                    rate={formData.room_rate}
                    businessUnitId={businessUnitId}
                    onSuccess={() => {
                      setShowEditRatePopup(false);
                      setReloadFlag((v) => v + 1);
                    }}
                    onCancel={() => setShowEditRatePopup(false)}
                  />
                )}

                <div className="erp-toolbar">
                  {(demoModeActive || permissions["revenue.create_price"]) && (
                    isDerived ? (
                      <button
                        className="erp-btn erp-btn--red"
                        onClick={handleUnlinkFromBase}
                        disabled={demoModeActive || unlinkLoading}
                        title={demoModeActive ? demoReadOnlyTitle : undefined}
                      >
                        {unlinkLoading ? "Unlinking..." : "Unlink from Base Rate"}
                      </button>
                    ) : (
                      isMother ? (
                        <span className="erp-btn erp-btn--green" style={{ cursor: "default" }}>
                          Base Rate
                        </span>
                      ) : (
                        <button
                          className="erp-btn erp-btn--green"
                          disabled={demoModeActive}
                          title={demoModeActive ? demoReadOnlyTitle : undefined}
                          onClick={() => {
                            if (demoModeActive || isDemoMode()) return;
                            setLinkForm(createInitialLinkForm());
                            setShowLinkDerivedPopup(true);
                          }}
                        >
                          Link to Base Rate
                        </button>
                      )
                    )
                  )}

                  {isDerived ? (
                    (demoModeActive || permissions["revenue.create_price"]) && (
                      <button className="erp-btn erp-btn--cyan" disabled={demoModeActive} title={demoModeActive ? demoReadOnlyTitle : undefined} onClick={() => { if (!demoModeActive && !isDemoMode()) setShowPopup(true); }}>
                        Calculate from Base Rate
                      </button>
                    )
                  ) : (
                    <>
                      {(demoModeActive || permissions["revenue.create_price"]) && (
                        <button className="erp-btn erp-btn--cyan" disabled={demoModeActive} title={demoModeActive ? demoReadOnlyTitle : undefined} onClick={() => { if (!demoModeActive && !isDemoMode()) setShowPopup(true); }}>
                          Add Rate Price
                        </button>
                      )}
                      {(demoModeActive || permissions["revenue.delete_price"]) && (
                        <button className="erp-btn erp-btn--red" disabled={demoModeActive} title={demoModeActive ? demoReadOnlyTitle : undefined} onClick={() => { if (!demoModeActive && !isDemoMode()) setShowRemoveTariffPopup(true); }}>
                          Remove Rate Prices
                        </button>
                      )}
                    </>
                  )}

                  <button className="erp-btn erp-btn--horizon" disabled={demoModeActive} title={demoModeActive ? demoReadOnlyTitle : undefined} onClick={() => { if (!demoModeActive && !isDemoMode()) setShowMinStayPopup(true); }}>
                    Add Min Stay
                  </button>
                  <button className="erp-btn erp-btn--horizon" disabled={demoModeActive} title={demoModeActive ? demoReadOnlyTitle : undefined} onClick={() => { if (!demoModeActive && !isDemoMode()) setShowMaxStayPopup(true); }}>
                    Add Max Stay
                  </button>

                  {(demoModeActive || permissions["revenue.manage_stop_sells"]) && (
                    <button className="erp-btn erp-btn--horizon" disabled={demoModeActive} title={demoModeActive ? demoReadOnlyTitle : undefined} onClick={() => { if (!demoModeActive && !isDemoMode()) setShowStopSellPopup(true); }}>
                      Add Stop Sell
                    </button>
                  )}
                  {(demoModeActive || permissions["revenue.manage_stop_sells"]) && (
                    <button className="erp-btn erp-btn--green" disabled={demoModeActive} title={demoModeActive ? demoReadOnlyTitle : undefined} onClick={() => { if (!demoModeActive && !isDemoMode()) setShowRemoveStopSellPopup(true); }}>
                      Remove Stop Sell
                    </button>
                  )}

                  <button
                    className={`erp-btn ${formData.room_rate?.status === "active" ? "erp-btn--red" : "erp-btn--green"}`}
                    disabled={demoModeActive}
                    title={demoModeActive ? demoReadOnlyTitle : undefined}
                    onClick={async () => {
                      if (demoModeActive || isDemoMode()) return;
                      const confirmToggle = window.confirm(
                        `Are you sure you want to ${formData.room_rate?.status === "active" ? "deactivate" : "activate"} this Room Rate?`
                      );
                      if (!confirmToggle) return;

                      try {
                        await axios.put(`${process.env.REACT_APP_API_URL}/api/roomRates/${formData.room_rate?.id}`, {
                          status: formData.room_rate?.status === "active" ? "inactive" : "active",
                        });
                        setFormData((prev) => ({
                          ...prev,
                          room_rate: {
                            ...prev.room_rate,
                            status: prev.room_rate?.status === "active" ? "inactive" : "active",
                          },
                        }));
                      } catch (err) {
                        console.error("Error updating room rate status:", err);
                        alert("Error updating room rate status.");
                      }
                    }}
                  >
                    {formData.room_rate?.status === "active" ? "Deactivate" : "Activate"}
                  </button>
                </div>
              </div>

              <div className="erp-grid">
                <section className="erp-panel">
                  <div className="erp-panelHead">
                    <h4 className="erp-panelTitle">Rate Next 365 Days</h4>
                    <div className="erp-panelSub">
                      Top chart shows a 90-day zoom. Bottom chart is a 365-day navigator.
                    </div>
                  </div>

                  <div className="erp-chartWrap">
                    {permissions["revenue.view_all_prices"] ? (
                      internalChartModel.hasData ? (
                        <>
                          <div className="erp-chartMain">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                data={internalChartViewport.rows}
                                margin={{ top: 14, right: 24, left: 4, bottom: 4 }}
                                onClick={(event) => {
                                  const selectedDate = event?.activePayload?.[0]?.payload?.date;
                                  if (selectedDate) setFocusDateInternal(selectedDate);
                                }}
                              >
                                <CartesianGrid strokeDasharray="3 3" stroke={isLightTheme ? "#d7e2ec" : "rgba(99,193,199,0.18)"} />
                                <XAxis
                                  dataKey="dateTs"
                                  type="number"
                                  scale="time"
                                  domain={[internalChartViewport.minTs, internalChartViewport.maxTs]}
                                  stroke={isLightTheme ? "#7c90a7" : "#aebbd0"}
                                  tick={{ fill: isLightTheme ? "#5b6680" : "#aebbd0" }}
                                  tickFormatter={(ts) => new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })}
                                />
                                <YAxis
                                  stroke={isLightTheme ? "#7c90a7" : "#aebbd0"}
                                  tick={{ fill: isLightTheme ? "#5b6680" : "#aebbd0" }}
                                  width={72}
                                  tickFormatter={(value) => `${Number(value).toFixed(0)} ${baseCurrency}`}
                                />
                                <Tooltip content={<CustomTooltip />} />

                                {internalChartViewport.stopSellRanges.map((range, idx) => (
                                  <ReferenceArea
                                    key={`ss-${idx}`}
                                    x1={range.x1}
                                    x2={range.x2}
                                    ifOverflow="hidden"
                                    fill="rgba(155, 52, 69, 0.13)"
                                    stroke="rgba(155, 52, 69, 0.45)"
                                    strokeOpacity={0.35}
                                  />
                                ))}

                                {focusHighlightRange && (
                                  <ReferenceArea
                                    x1={focusHighlightRange.start}
                                    x2={focusHighlightRange.end}
                                    fill={isLightTheme ? "rgba(61,182,233,0.12)" : "rgba(61,182,233,0.16)"}
                                    stroke={isLightTheme ? "rgba(34,58,122,0.55)" : "rgba(61,182,233,0.55)"}
                                    strokeOpacity={0.45}
                                  />
                                )}

                                {internalChartModel.seriesKeys.map((key) => (
                                  <Line
                                    key={key}
                                    type="monotone"
                                    dataKey={key}
                                    stroke={internalChartModel.colorByKey[key]}
                                    strokeWidth={2.2}
                                    dot={false}
                                    connectNulls={false}
                                    isAnimationActive={false}
                                  />
                                ))}
                              </LineChart>
                            </ResponsiveContainer>
                          </div>

                          <div className="erp-chartMini">
                            <div className="erp-chartMiniText">
                              365-day overview. Click or drag to move the 90-day zoom chart above.
                            </div>
                            <div className="erp-chartMiniPlot">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                  data={internalChartModel.allRows}
                                  margin={{ top: 8, right: 24, left: 4, bottom: 2 }}
                                  onClick={(event) => {
                                    const selectedDate = event?.activePayload?.[0]?.payload?.date;
                                    if (selectedDate) setInternalNavigatorDate(selectedDate);
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="2 2" stroke={isLightTheme ? "#d7e2ec" : "rgba(99,193,199,0.14)"} />
                                  <XAxis
                                    dataKey="dateTs"
                                    type="number"
                                    scale="time"
                                    domain={[internalChartModel.minTs, internalChartModel.navigatorMaxTs]}
                                    stroke={isLightTheme ? "#7c90a7" : "#aebbd0"}
                                    tick={{ fill: isLightTheme ? "#5b6680" : "#aebbd0" }}
                                    interval="preserveStartEnd"
                                    tickCount={6}
                                    minTickGap={56}
                                    tickFormatter={(ts) => new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })}
                                  />
                                  <YAxis
                                    stroke={isLightTheme ? "#7c90a7" : "#aebbd0"}
                                    tick={{ fill: isLightTheme ? "#5b6680" : "#aebbd0" }}
                                    width={44}
                                    tickFormatter={(value) => Number(value).toFixed(0)}
                                  />

                                  <ReferenceArea
                                    x1={internalChartViewport.minTs}
                                    x2={internalChartViewport.maxTsWithTail}
                                    fill={isLightTheme ? "rgba(61,182,233,0.10)" : "rgba(61,182,233,0.14)"}
                                    stroke={isLightTheme ? "rgba(34,58,122,0.55)" : "rgba(61,182,233,0.55)"}
                                    strokeOpacity={0.55}
                                  />

                                  {internalChartModel.seriesKeys.map((key) => (
                                    <Line
                                      key={`mini-${key}`}
                                      type="monotone"
                                      dataKey={key}
                                      stroke={internalChartModel.colorByKey[key]}
                                      strokeWidth={1.1}
                                      strokeOpacity={0.64}
                                      dot={false}
                                      connectNulls={false}
                                      isAnimationActive={false}
                                    />
                                  ))}
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                            <div className="erp-chartMiniBrush">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                  data={internalChartModel.allRows}
                                  margin={{ top: 0, right: 24, left: 4, bottom: 0 }}
                                >
                                  {internalChartModel.seriesKeys[0] && (
                                    <Line
                                      type="monotone"
                                      dataKey={internalChartModel.seriesKeys[0]}
                                      stroke="rgba(0,0,0,0)"
                                      dot={false}
                                      isAnimationActive={false}
                                    />
                                  )}
                                  <Brush
                                    dataKey="dateTs"
                                    height={18}
                                    travellerWidth={8}
                                    stroke={isLightTheme ? "rgba(34,58,122,0.82)" : "rgba(61,182,233,0.82)"}
                                    fill={isLightTheme ? "rgba(61,182,233,0.14)" : "rgba(61,182,233,0.14)"}
                                    startIndex={navigatorBrushWindow.startIndex}
                                    endIndex={navigatorBrushWindow.endIndex}
                                    onChange={handleNavigatorBrushChange}
                                    tickFormatter={() => ""}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="erp-empty">No rate data available for this room rate.</div>
                      )
                    ) : (
                      <div className="erp-empty">You do not have permission to view prices.</div>
                    )}
                  </div>

                  {internalChartModel.seriesKeys.length > 0 && (
                    <div className="erp-legend">
                      {internalChartModel.seriesKeys.map((key) => (
                        <span key={`legend-${key}`} className="erp-legendItem">
                          <span className="erp-legendDot" style={{ background: internalChartModel.colorByKey[key] }} />
                          Stay: {key}
                        </span>
                      ))}
                      {internalChartModel.stopSellRanges.length > 0 && (
                        <span className="erp-legendItem">
                          <span className="erp-legendDot" style={{ background: "#9B3445" }} />
                          Stop Sell
                        </span>
                      )}
                    </div>
                  )}
                </section>

                <aside className="erp-side">
                  <div className="erp-panel">
                    <div className="erp-tableWrap erp-tableWrap--priority">
                      {permissions["revenue.view_all_prices"] ? (
                        <StayLengthTable
                          priceHistory={priceHistory}
                          stopSells={stopSells}
                          roomRateValidUntil={demoModeActive ? formData.room_rate?.valid_to : formData.room_rate?.valid_until}
                          roomRateId={formData.room_rate_id}
                          focusDate={focusDateInternal}
                          clearFocusDate={() => setFocusDateInternal(null)}
                          isLightTheme={isLightTheme}
                          referenceDate={demoModeActive ? referenceDate : undefined}
                          currency={baseCurrency}
                        />
                      ) : (
                        <div className="erp-empty" style={{ minHeight: 160 }}>No permission to view prices.</div>
                      )}
                    </div>
                  </div>
                </aside>
              </div>
            </>
          )}

        {!demoModeActive && showPopup && (
          <NewRatePricePopup
            show={showPopup}
            onClose={(refresh) => {
              setShowPopup(false);
              if (refresh) setReloadFlag((v) => v + 1);
            }}
            roomRate={formData.room_rate}
            derivedMode={isDerived}
            linkedToText={linkedToText}
            calcText={calcText}
          />
        )}

        {!demoModeActive && showMinStayPopup && (
          <NewStayConstraintPopup
            show={showMinStayPopup}
            type="min"
            roomRate={formData.room_rate}
            onClose={(refresh) => {
              setShowMinStayPopup(false);
              if (refresh) setReloadFlag((v) => v + 1);
            }}
          />
        )}

        {!demoModeActive && showMaxStayPopup && (
          <NewStayConstraintPopup
            show={showMaxStayPopup}
            type="max"
            roomRate={formData.room_rate}
            onClose={(refresh) => {
              setShowMaxStayPopup(false);
              if (refresh) setReloadFlag((v) => v + 1);
            }}
          />
        )}

        {!demoModeActive && showStopSellPopup && (
          <AddStopSellPopup
            show={showStopSellPopup}
            onClose={() => {
              setShowStopSellPopup(false);
            }}
            formData={formData}
          />
        )}

        {!demoModeActive && showRemoveStopSellPopup && (
          <RemoveStopSellPopup
            show={showRemoveStopSellPopup}
            onClose={() => {
              setShowRemoveStopSellPopup(false);
            }}
            formData={formData}
            stopSells={stopSells}
          />
        )}

        {!demoModeActive && showRemoveTariffPopup && (
          <RemoveRatePricesPopup
            show={showRemoveTariffPopup}
            onClose={() => {
              setShowRemoveTariffPopup(false);
            }}
            formData={formData}
            availableStayRanges={availableStayRanges}
          />
        )}
 
        

        {!demoModeActive && activeTab === "market" && (
          <>

          
            {/* Tarjeta izquierda con la tabla */}
            <div className="erp-marketTablePanel">

              
              <div className="erp-marketSurface">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="erp-marketTitle">
                    Market Rate Prices -{" "}
                    {focusDateMarket
                      ? `From ${format(new Date(focusDateMarket), "dd/MM/yyyy")} (14 days)`
                      : "Next 14 Days"}
                  </h5>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="erp-marketHint">
                    Tip: click on the graph to view data from a different date.
                  </p>
                  {focusDateMarket && (
                    <button
                      onClick={() => setFocusDateMarket(null)}
                      className={`btn btn-sm px-2 py-0 ${isLightTheme ? "btn-outline-secondary" : "btn-outline-light"}`}
                      style={{
                        fontSize: "0.7rem",
                        lineHeight: "1",
                        borderRadius: "4px",
                        padding: "2px 6px"
                      }}
                    >
                      Back to today
                    </button>
                  )}
                </div>


                  {(() => {
                    const currentRateId = formData.room_rate?.id || formData.room_rate_id;
                    const hasRule = settings.market_price_rules?.some(r => r.room_rate_id === currentRateId);

                    if (hasRule) {
                      return (
                        <button
                          className="erp-btn erp-btn--red"
                          onClick={async () => {
                            const confirmed = window.confirm("This will deactivate the automatic market pricing rule for this rate. Continue?");
                            if (!confirmed) return;

                            try {
                              const updatedRules = settings.market_price_rules.filter(r => r.room_rate_id !== currentRateId);
                              await axios.put(`${process.env.REACT_APP_API_URL}/api/hotel-settings/${businessUnitId}`, {
                                ...settings,
                                market_price_rules: updatedRules
                              });

                              alert("Rule deactivated.");
                              setReloadFlag((v) => v + 1);
                            } catch (err) {
                              console.error("Ã¢ÂÅ’ Error disabling rule:", err);
                              alert("Failed to deactivate rule.");
                            }
                          }}
                        >
                          Deactivate Rule
                        </button>
                      );
                    }

                    if (permissions["revenue.apply_market_prices"]) {
                      const isScraper = settings.rms_source === "Scraper";

                      return (
                        <div className="d-flex gap-2">
                          <button
                            className="erp-btn erp-btn--cyan"
                            onClick={() => setShowApplyPricePopup(true)}
                            disabled={marketPrices.length === 0}
                            title={marketPrices.length === 0 ? "No market prices available yet" : ""}
                          >
                            Apply Market Prices
                          </button>

                          
                        </div>
                      );
                    }


                  })()}

                </div>

           

                {marketPrices.length === 0 ? (
                  <p className="erp-empty erp-empty--compact">No market prices found.</p>
                ) : (
                  <div className="erp-marketTableWrap">
                    <table
                      className="erp-marketTable"
                    >
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Market ({baseCurrency})</th>
                          {permissions["revenue.view_all_prices"] && <th>Your ({baseCurrency})</th>}
                          {permissions["revenue.view_all_prices"] && <th>Delta ({baseCurrency})</th>}
                          {permissions["revenue.view_all_prices"] && <th>Delta (%)</th>}
                          <th>Status</th>
                          <th>Source</th>
                          <th>Origin</th>
                          <th>Curr.</th>
                        </tr>
                      </thead>
                      <tbody>
                      {expandedMarketPrices
                        .filter((item) => {
                          const base = focusDateMarket ? new Date(focusDateMarket) : new Date();
                          base.setHours(0, 0, 0, 0);
                          const limit = new Date(base);
                          limit.setDate(base.getDate() + 14);

                          const itemDate = new Date(item.date);
                          itemDate.setHours(0, 0, 0, 0);

                          return itemDate >= base && itemDate < limit;
                        })
                        .map((item, idx) => {
                          const dateStr = item.date;

                          const real = realPricesByDate[dateStr]?.price;
                          const isInactive = formData?.room_rate?.status !== "active";
                          const isStopped = stopSellDateSet.has(dateStr);
                          const noRate = real === undefined;

                          const diff = !noRate ? real - parseFloat(item.price) : null;
                          const diffPct = !noRate ? (diff / parseFloat(item.price)) * 100 : null;
                          let color = "inherit";
                          if (diff !== null) {
                            if (diff > 0) color = "green";
                            else if (diff < 0) color = "red";
                            // Si diff === 0, se queda como "inherit"
                          }


                          return (
                            <tr key={idx}>
                              <td>{dateStr}</td>
                              <td>{formatRatePriceAmount(item.price, baseCurrency, currencyCatalog)}</td>
                              {permissions["revenue.view_all_prices"] && (
                                <td>{noRate
                                  ? "No rate"
                                  : formatRatePriceAmount(real, baseCurrency, currencyCatalog)}</td>
                              )}
                              {permissions["revenue.view_all_prices"] && (
                              <td style={{ color }}>{diff !== null
                                ? formatRatePriceAmount(diff, baseCurrency, currencyCatalog)
                                : "-"}</td>
                              )}
                              {permissions["revenue.view_all_prices"] && (
                              <td style={{ color }}>{diffPct !== null ? `${diffPct.toFixed(1)} %` : "-"}</td>
                               )}
                              <td>
                                {isInactive ? "INACTIVE" : isStopped ? "STOP SELL" : "OPEN"}
                              </td>
                              <td>{item.source}</td>
                              <td>{item.origin}</td>
                              <td>{item.currency}</td>
                            </tr>
                          );
                        })}

                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            {/* Tarjeta derecha con el grÃƒÂ¡fico */}
            <div className="erp-marketChartPanel">
              <h5 className="erp-marketTitle">Rate Comparison Chart</h5>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={marketChartData}
                  onClick={(e) => {
                    const dateStr = e?.activeLabel;
                    if (dateStr) {
                      setFocusDateMarket(dateStr);
                    }
                  }}
                >


                  <CartesianGrid strokeDasharray="3 3" stroke={isLightTheme ? "#d7e2ec" : "rgba(99,193,199,0.18)"} />
                  <XAxis dataKey="date" stroke={isLightTheme ? "#7c90a7" : "#aebbd0"} tick={{ fill: isLightTheme ? "#5b6680" : "#aebbd0" }} />
                  <YAxis stroke={isLightTheme ? "#7c90a7" : "#aebbd0"} tick={{ fill: isLightTheme ? "#5b6680" : "#aebbd0" }} />
                  <Tooltip content={<MarketTooltip />} />


                  <Legend />
                  {permissions["revenue.view_all_prices"] && (
                  <Line
                    type="monotone"
                    dataKey="YourRate"
                    stroke="#3DB6E9"
                    strokeWidth={2}
                    name="Your Rate"
                    dot={false}
                    connectNulls={false}
                  />
                  )}
                  <Line
                    type="monotone"
                    dataKey="Market"
                    stroke="#63C1C7"
                    strokeWidth={2}
                    name="Market Rate"
                    dot={false}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {!demoModeActive && showApplyPricePopup && (
          <div
            className="erp-inlineModalOverlay position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          >
            <div
              className="erp-inlineModal position-relative"
              style={{
                width: "480px",
                maxHeight: "90vh",
                overflowY: "auto",
                padding: "24px",
                borderRadius: "16px"
              }}
            >

              {/* Ã°Å¸â€â€™ Overlay de bloqueo mientras aplica */}
              {isApplying && (
                <div
                  className="position-absolute top-0 start-0 d-flex flex-column align-items-center justify-content-center"
                  style={{
                    width: "100%",
                    height: "100%",
                    background: "rgba(0,0,0,0.6)",
                    backdropFilter: "blur(3px)",
                    zIndex: 2000,
                    borderRadius: "8px"
                  }}
                >
                  <div className="spinner-border erp-spinner mb-3" role="status" />
                  <div className="fw-bold">Applying Market Prices...</div>
                  <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                    Please wait
                  </div>
                </div>
              )}

              <h5 className="erp-inlineModalTitle mb-4 text-center">Apply prices from market</h5>


              <div className="mb-3">
                <label className="form-label">Date selection mode</label>
                <select
                  className="form-select form-select-sm"
                  value={dateSelectionMode}
                  onChange={(e) => setDateSelectionMode(e.target.value)}
                  disabled={isApplying}
                >
                  <option value="range">Range</option>
                  <option value="individual">Individual dates</option>
                </select>
              </div>

              {dateSelectionMode === "range" && (
                <div className="mb-3 d-flex gap-2">
                  <div className="flex-fill">
                    <label className="form-label">From</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(e.target.value)}
                      min={marketPrices?.[0]?.start_date}
                      max={marketPrices?.[marketPrices.length - 1]?.start_date}
                    />

                  </div>
                  <div className="flex-fill">
                    <label className="form-label">To</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(e.target.value)}
                      min={marketPrices?.[0]?.start_date}
                      max={marketPrices?.[marketPrices.length - 1]?.start_date}
                    />

                  </div>
                </div>
              )}

              {dateSelectionMode === "individual" && (
                <div className="erp-inlineModalList mb-3" style={{ maxHeight: "160px", overflowY: "auto", padding: "8px", borderRadius: "8px" }}>
                  {marketPrices.map((item) => {
                    const date = item.start_date;
                    const market = parseFloat(item.price);
                    const real = realPricesByDate[date]?.price;
                    const isInactive = formData?.room_rate?.status !== "active";
                    const isStopped = stopSellDateSet.has(date);
                    const noRate = isInactive || isStopped || real === undefined;

                    const diff = !noRate ? real - market : null;
                    const diffPct = !noRate ? ((real - market) / market) * 100 : null;

                    const isChecked = selectedDates.includes(date);

                    return (
                      <div key={date} className="d-flex align-items-center justify-content-between mb-1">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`checkbox-${date}`}
                            disabled={noRate || isApplying}
                            checked={isChecked}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedDates((prev) =>
                                checked ? [...prev, date] : prev.filter((d) => d !== date)
                              );
                            }}
                          />
                          <label className="form-check-label" htmlFor={`checkbox-${date}`}>
                            {date}
                          </label>
                        </div>

                        <div style={{ fontSize: "0.75rem", textAlign: "right", width: "60%" }}>
                          <div>Market: {formatRatePriceAmount(market, baseCurrency, currencyCatalog)}</div>
                          <div>Your rate: {noRate
                            ? "No rate"
                            : formatRatePriceAmount(real, baseCurrency, currencyCatalog)}</div>
                          <div
                            style={{
                              color:
                                diff === null
                                  ? "inherit"
                                  : diff > 0
                                  ? "#216b5a"
                                  : diff < 0
                                  ? "#9b3445"
                                  : "inherit"
                            }}
                          >
                            Delta {baseCurrency}: {diff !== null
                              ? formatRatePriceNumber(diff, baseCurrency, currencyCatalog)
                              : "-"} / Delta %:{" "}
                            {diffPct !== null ? diffPct.toFixed(1) + "%" : "-"}
                          </div>

                        </div>
                      </div>
                    );
                  })}

                </div>
              )}


              <div className="mb-3">
                <label className="form-label">Adjustment</label>
                <select
                  className="form-select form-select-sm"
                  value={adjustType}
                  onChange={(e) => setAdjustType(e.target.value)}
                >
                  <option value="equal">Same as Market</option>
                  <option value="above">Above Market</option>
                  <option value="below">Below Market</option>
                </select>
              </div>

              {adjustType !== "equal" && (
                <>
                  <div className="mb-3">
                    <label className="form-label">Value of the Adjustment</label>
                    <input
                      type="number"
                      inputMode={
                        adjustUnit === "absolute" && currencyMinorUnits === 0
                          ? "numeric"
                          : "decimal"
                      }
                      step={
                        adjustUnit === "absolute"
                          ? currencyAmountStep(baseCurrency, currencyCatalog)
                          : "0.01"
                      }
                      className="form-control form-control-sm"
                      value={adjustValue}
                      onChange={(e) => setAdjustValue(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Type of Adjustment</label>
                    <select
                      className="form-select form-select-sm"
                      value={adjustUnit}
                      onChange={(e) => setAdjustUnit(e.target.value)}
                    >
                      <option value="absolute">{baseCurrency} (Value)</option>
                      <option value="percent">% (Percent)</option>
                    </select>
                  </div>
                  

                </>
              )}

              

              <div className="d-flex justify-content-between mt-4">
                <button
                  className="btn btn-sm fw-bold px-3"
                  style={{
                    background: "var(--erp-danger-bg)",
                    border: "1px solid var(--erp-danger-solid)",
                    color: "var(--erp-danger)",
                    width: "48%"
                  }}
                  onClick={() => {
                    resetPopupState();
                    setShowApplyPricePopup(false);
                  }}
                  disabled={isApplying}

                >
                  Cancel
                </button>

                <button
                  className="btn btn-sm fw-bold px-3"
                  style={{
                    background: "#223a7a",
                    border: "1px solid var(--erp-horizon)",
                    color: "white",
                    width: "48%",
                    opacity: isApplying ? 0.6 : 1,
                    cursor: isApplying ? "not-allowed" : "pointer"
                  }}
                  disabled={isApplying}
                  onClick={async () => {
                    try {
                      console.log("[ApplyMarketPrices] Click Apply Ã¢â€ â€™ start");
                      setIsApplying(true);
                      await handleApplyMarketPrices(); // Ã¢Å¡Â Ã¯Â¸Â si tu funciÃƒÂ³n no es async, sigue funcionando igual
                      console.log("[ApplyMarketPrices] Done");
                    } catch (err) {
                      console.error("Ã¢ÂÅ’ [ApplyMarketPrices] Error:", err);
                      alert("An error occurred while applying market prices.");
                    } finally {
                      setIsApplying(false);
                    }
                  }}
                >
                  {isApplying ? "Applying..." : "Apply"}
                </button>

              </div>
            </div>
          </div>
        )}
    

          </>
        )}
        

        {/* NEW: Link Derived Rate Popup */}
        {!demoModeActive && showLinkDerivedPopup && (
          derivedRateMode === "complex" ? (
            <ComplexFormulaPopup
              onClose={() => setShowLinkDerivedPopup(false)}
              formData={{ ...formData, baseCandidates }}
            />

          ) : (
            <div
              className="erp-inlineModalOverlay"
              style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                display: "flex",
                alignItems: "center", justifyContent: "center", zIndex: 9999
              }}
              onClick={() => setShowLinkDerivedPopup(false)}
            >
              <div
                className="erp-inlineModal"
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: 520,
                  maxWidth: "90%",
                  borderRadius: 16,
                  padding: 16,
                }}
              >
                <h4 className="erp-inlineModalTitle" style={{ marginBottom: 8 }}>Link to Base Rate</h4>
                <p style={{ fontSize: 12, opacity: 0.8, marginTop: 0 }}>
                  Select a base rate and define the margin. Linking does not modify existing prices.
                  Prices will only update when you change prices explicitly.
                </p>


                {/* Base rate selector */}
                <div className="mb-3">
                  <label className="form-label">Base Rate</label>
                  <select
                    className="form-select"
                    value={linkForm.base_rate_id || ""}
                    onChange={(e) =>
                      setLinkForm((prev) => ({
                        ...prev,
                        base_rate_id: Number(e.target.value) || null,
                      }))
                    }
                  >
                    <option value="">-- Select a base rate --</option>
                    {baseCandidates.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.rate_code})
                      </option>
                    ))}
                  </select>
                  <div className="erp-inlineModalHint" style={{ fontSize: 12, marginTop: 4 }}>
                    Rates from any category are listed. Currency must match.
                  </div>
                </div>

                {/* Adjustment */}
                <div className="mb-3" style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Adjustment Type</label>
                    <select
                      className="form-select"
                      value={linkForm.adjustment_type}
                      onChange={(e) =>
                        setLinkForm((prev) => ({ ...prev, adjustment_type: e.target.value }))
                      }
                    >
                      <option value="percent">Percent</option>
                      <option value="absolute">Absolute</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Adjustment Value</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="form-control"
                      placeholder="e.g. -10 or 5"
                      value={linkForm.adjustment_value}
                      autoFocus
                      onChange={(e) =>
                        setLinkForm((prev) => ({
                          ...prev,
                          adjustment_value: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                {/* Actions */}
                <div
                  style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
                >
                  <button
                    className="btn btn-sm"
                    onClick={() => setShowLinkDerivedPopup(false)}
                    style={{
                      border: "1px solid var(--erp-border-strong)",
                      color: "var(--erp-text)",
                      background: "var(--erp-surface-soft)",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={async () => {
                      if (demoModeActive || isDemoMode()) return;
                      if (!linkForm.base_rate_id) {
                        alert("Please select a base rate.");
                        return;
                      }
                      if (
                        !["percent", "absolute"].includes(linkForm.adjustment_type)
                      ) {
                        alert("Invalid adjustment type.");
                        return;
                      }
                      const normalizedAdjustmentRaw = String(linkForm.adjustment_value ?? "")
                        .trim()
                        .replace(",", ".");
                      if (normalizedAdjustmentRaw === "") {
                        alert("Please enter an adjustment value.");
                        return;
                      }
                      const parsedAdjustmentValue = Number(normalizedAdjustmentRaw);
                      if (!Number.isFinite(parsedAdjustmentValue)) {
                        alert("Invalid adjustment value.");
                        return;
                      }

                      try {
                        const id = formData?.room_rate?.id;
                        console.log("[DerivedRates][Link] PUT /api/derived-rates/:id payload:", {
                          base_rate_id: linkForm.base_rate_id,
                          adjustment_type: linkForm.adjustment_type,
                          adjustment_value: parsedAdjustmentValue,
                        });

                        await axios.put(
                          `${process.env.REACT_APP_API_URL}/api/derived-rates/${id}`,
                          {
                            base_rate_id: linkForm.base_rate_id,
                            adjustment_type: linkForm.adjustment_type,
                            adjustment_value: parsedAdjustmentValue,
                          }
                        );

                        alert("Base rate link saved. Existing prices were not modified.");
                        setShowLinkDerivedPopup(false);
                        setReloadFlag((v) => v + 1);

                      } catch (err) {
                        console.error(
                          "[Apply] Error linking derived rate:",
                          err
                        );
                        alert(
                          err?.response?.data?.error ||
                            "Error linking derived rate."
                        );
                      }
                    }}
                    style={{
                      border: "1px solid var(--erp-horizon)",
                      color: "#fff",
                      background: "#223a7a",
                    }}
                  >
                    Save & Link
                  </button>
                </div>
              </div>
            </div>
          )
        )}



        {!demoModeActive && showScraperApplyPopup && (
          <ApplyScrapedPricesPopup
            sources={applySources}
            onClose={() => setShowScraperApplyPopup(false)}
            onApply={handleApplyScrapedPrices}
          />
        )}

        











      </div>
    </div>
    </div>
    </>

  );
};
const PriceTooltip = ({ hotels, nights, mode, currencyCode, currencyCatalog = [] }) => {
  if (mode !== "area") return null;

  if (!hotels || hotels.length === 0) return null;

  return (
    <div style={{
      backgroundColor: "#121f3d",
      color: "#f2f6ff",
      padding: "10px",
      borderRadius: "6px",
      fontSize: "13px",
      boxShadow: "0 12px 28px rgba(2,8,28,0.32)",
      maxWidth: "300px",
      border: "1px solid rgba(61,182,233,0.58)"
    }}>
      {hotels.map((h, i) => {
        const perNight = h.parsedPrice / nights;
        return (
          <div key={i}>
            <b>{h.name}</b> - {formatRatePriceAmount(
              perNight,
              currencyCode,
              currencyCatalog
            )} / night
          </div>
        );
      })}
    </div>
  );
};






export default EditRatePrice;
